// Recovery-supporting meals — simple, healthy, achievable
// All images are AI-generated originals (Gemini Nano Banana via
// /app/scripts/generate_meal_images.py) — one bespoke image per recipe.
// They are illustrative, not exact representations of the prepared dish.
//
// Tags: vegetarian | vegan | gluten-free | dairy-free | high-protein | budget | quick
// Categories: breakfast | lunch | dinner | dessert | snacks | smoothies
// Recovery rationales focus on blood-sugar stability, mood-support nutrients
// (B-vits, omega-3, magnesium, zinc), gut-brain axis, hydration, satiety.

import { DIET_EXPANSION_MAINS, DIET_EXPANSION_DESSERTS } from './dietMealsExpansion';

export const DIET_DISCLAIMER =
  "Photos are AI-generated illustrations of each dish, not exact representations. Recipes are designed to be simple, affordable, and supportive of long-term Recovery. Always consult your GP or dietitian if you have allergies, chronic conditions, or specific dietary needs.";

export const MEAL_CATEGORIES = [
  { id: 'breakfast', label: 'Breakfast', icon: '🌅' },
  { id: 'lunch', label: 'Lunch', icon: '🥗' },
  { id: 'dinner', label: 'Dinner', icon: '🍽️' },
  { id: 'dessert', label: 'Dessert', icon: '🍮' },
  { id: 'snacks', label: 'Snacks', icon: '🥨' },
  { id: 'smoothies', label: 'Smoothies', icon: '🥤' },
];

export const MEAL_COUNTRIES = [
  { id: 'all', label: 'All', flag: '🌍' },
  { id: 'western', label: 'Western', flag: '🥗' },
  { id: 'italy', label: 'Italian', flag: '🇮🇹' },
  { id: 'france', label: 'French', flag: '🇫🇷' },
  { id: 'spain', label: 'Spanish', flag: '🇪🇸' },
  { id: 'portugal', label: 'Portuguese', flag: '🇵🇹' },
  { id: 'greece', label: 'Greek', flag: '🇬🇷' },
  { id: 'russia', label: 'Russian', flag: '🇷🇺' },
  { id: 'ukraine', label: 'Ukrainian', flag: '🇺🇦' },
  { id: 'korea', label: 'Korean', flag: '🇰🇷' },
  { id: 'india', label: 'Indian', flag: '🇮🇳' },
  { id: 'mexico', label: 'Mexican', flag: '🇲🇽' },
  { id: 'asia', label: 'Asian', flag: '🌏' },
  { id: 'middle-east', label: 'Middle East', flag: '🌗' },
];

const img = (id) => `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=800&q=70`;

export const DIET_MEALS = [
  // ───────────── BREAKFAST (6) ─────────────
  {
    id: 'b1',
    title: 'Steel-Cut Oats with Berries & Walnuts',
    category: 'breakfast',
    image: img('1495214783159-3503fd1b572d'),
    prep_minutes: 5, cook_minutes: 20, difficulty: 'easy',
    tags: ['vegetarian', 'high-fibre', 'budget'],
    ingredients: [
      '1/2 cup steel-cut oats',
      '2 cups water or unsweetened milk',
      '1/2 cup mixed berries (fresh or frozen)',
      '2 tbsp chopped walnuts',
      '1 tsp cinnamon',
      '1 tsp honey or maple syrup (optional)',
    ],
    steps: [
      'Bring water/milk to a boil in a small saucepan.',
      'Stir in oats and cinnamon, reduce heat to low.',
      'Simmer uncovered, stirring occasionally, 18–20 minutes.',
      'Spoon into a bowl, top with berries and walnuts.',
      'Drizzle with honey if desired. Serve warm.',
    ],
    recovery_note:
      'Steel-cut oats release glucose slowly, preventing the blood-sugar spikes that fuel cravings. Walnuts add omega-3s for mood, berries add antioxidants for brain repair.',
  },
  {
    id: 'b2',
    title: 'Two-Egg Veggie Scramble',
    category: 'breakfast',
    image: img('1525351484163-7529414344d8'),
    prep_minutes: 5, cook_minutes: 8, difficulty: 'easy',
    tags: ['vegetarian', 'gluten-free', 'high-protein', 'quick'],
    ingredients: [
      '2 eggs',
      '1/4 cup baby spinach',
      '2 cherry tomatoes, halved',
      '1 tbsp grated cheese',
      '1 tsp olive oil',
      'Salt, pepper to taste',
    ],
    steps: [
      'Whisk eggs in a bowl with a pinch of salt and pepper.',
      'Heat olive oil in a non-stick pan over medium heat.',
      'Add tomatoes and spinach, sauté 1 minute until spinach wilts.',
      'Pour in eggs and stir gently until just set, about 2–3 minutes.',
      'Top with cheese, fold and serve.',
    ],
    recovery_note:
      'Eggs deliver complete protein and choline. Both help repair brain chemistry damaged by long-term drinking or drug use.',
  },
  {
    id: 'b3',
    title: 'Greek Yoghurt Parfait',
    category: 'breakfast',
    image: img('1488477181946-6428a0291777'),
    prep_minutes: 5, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'high-protein', 'quick'],
    ingredients: [
      '3/4 cup plain Greek yoghurt',
      '1/4 cup granola',
      '1/2 cup mixed berries',
      '1 tbsp honey',
      '1 tbsp chia seeds (optional)',
    ],
    steps: [
      'Spoon half the yoghurt into a glass or bowl.',
      'Layer half the granola and berries.',
      'Repeat the layers.',
      'Drizzle with honey and sprinkle chia seeds.',
      'Serve immediately.',
    ],
    recovery_note:
      'Live cultures in Greek yoghurt support the gut-brain axis. A healthy gut is linked to better mood, which matters in early Recovery.',
  },
  {
    id: 'b4',
    title: 'Wholemeal Avocado Toast',
    category: 'breakfast',
    image: img('1541519227354-08fa5d50c44d'),
    prep_minutes: 5, cook_minutes: 3, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'quick'],
    ingredients: [
      '2 slices wholemeal sourdough',
      '1 ripe avocado',
      '1/2 lemon, juiced',
      'Pinch of chilli flakes',
      'Salt, pepper',
      '1 tbsp pumpkin seeds',
    ],
    steps: [
      'Toast bread until golden.',
      'Mash avocado with lemon, salt, pepper.',
      'Spread thickly onto toast.',
      'Sprinkle chilli flakes and pumpkin seeds.',
      'Serve immediately.',
    ],
    recovery_note:
      'Healthy fats in avocado support steady energy for hours, reducing the mid-morning sugar urges that often masquerade as alcohol cravings.',
  },
  {
    id: 'b5',
    title: 'Smoked Salmon & Cream Cheese Bagel',
    category: 'breakfast',
    image: img('1525351484163-7529414344d8'),
    prep_minutes: 5, cook_minutes: 2, difficulty: 'easy',
    tags: ['high-protein', 'omega-3'],
    ingredients: [
      '1 wholemeal bagel, halved',
      '2 tbsp light cream cheese',
      '60g smoked salmon',
      '2 thin slices red onion',
      '2 tsp capers',
      'Fresh dill, lemon wedge',
    ],
    steps: [
      'Toast the bagel halves until lightly golden.',
      'Spread each half with cream cheese.',
      'Layer smoked salmon, onion and capers.',
      'Top with dill, squeeze lemon over.',
      'Serve open-faced.',
    ],
    recovery_note:
      'Salmon is one of the richest dietary sources of omega-3 EPA and DHA. Both are shown in clinical trials to reduce depressive symptoms and support cognitive recovery.',
  },
  {
    id: 'b6',
    title: 'Banana Oat Pancakes',
    category: 'breakfast',
    image: img('1528207776546-365bb710ee93'),
    prep_minutes: 5, cook_minutes: 10, difficulty: 'easy',
    tags: ['vegetarian', 'gluten-free', 'budget'],
    ingredients: [
      '1 ripe banana',
      '2 eggs',
      '1/2 cup rolled oats',
      '1/2 tsp baking powder',
      '1 tsp cinnamon',
      'Coconut oil for frying',
    ],
    steps: [
      'Blend banana, eggs, oats, baking powder, cinnamon until smooth.',
      'Heat a little coconut oil in a non-stick pan over medium heat.',
      'Pour 1/4 cup batter per pancake.',
      'Cook 2 minutes per side until golden.',
      'Serve with berries and a drizzle of maple syrup.',
    ],
    recovery_note:
      'Bananas are loaded with B6, magnesium and tryptophan. All are needed for the brain to manufacture mood-stabilising serotonin.',
  },

  // ───────────── LUNCH (8) ─────────────
  {
    id: 'l1',
    title: 'Chickpea & Quinoa Buddha Bowl',
    category: 'lunch',
    image: img('1512621776951-a57141f2eefd'),
    prep_minutes: 10, cook_minutes: 15, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'high-protein'],
    ingredients: [
      '1/2 cup cooked quinoa',
      '1/2 cup tinned chickpeas, rinsed',
      '1 cup mixed leafy greens',
      '1/2 cup roasted sweet potato',
      '1/4 avocado, sliced',
      '2 tbsp tahini, 1 tbsp lemon juice, water',
    ],
    steps: [
      'Cook quinoa per packet or use leftovers.',
      'Toss chickpeas and diced sweet potato with olive oil; roast 200°C for 15 mins.',
      'Whisk tahini, lemon juice and water until pourable.',
      'Layer greens, quinoa, chickpeas, sweet potato, avocado in a bowl.',
      'Drizzle with tahini dressing and serve.',
    ],
    recovery_note:
      'A perfect anti-inflammatory plate: complete plant protein, complex carbs, healthy fats and fibre that keeps blood-sugar steady through the afternoon.',
  },
  {
    id: 'l2',
    title: 'Tuna & White Bean Salad',
    category: 'lunch',
    image: img('1505253758473-96b7015fcd40'),
    prep_minutes: 10, cook_minutes: 0, difficulty: 'easy',
    tags: ['high-protein', 'omega-3', 'budget', 'quick'],
    ingredients: [
      '1 tin tuna in olive oil, drained',
      '1 tin cannellini beans, rinsed',
      '1 cup cherry tomatoes, halved',
      '1/4 red onion, finely sliced',
      '2 tbsp olive oil, 1 tbsp red wine vinegar',
      'Fresh parsley, salt, pepper',
    ],
    steps: [
      'Combine tuna, beans, tomatoes and onion in a bowl.',
      'Whisk olive oil and vinegar; season.',
      'Pour over salad and toss gently.',
      'Top with parsley.',
      'Serve over leaves or with crusty bread.',
    ],
    recovery_note:
      'High protein + soluble fibre keeps insulin response gentle. The omega-3s in tuna are clinically associated with reduced cravings and improved mood.',
  },
  {
    id: 'l3',
    title: 'Hearty Lentil Soup',
    category: 'lunch',
    image: img('1547592180-85f173990554'),
    prep_minutes: 10, cook_minutes: 30, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'budget', 'high-fibre'],
    ingredients: [
      '1 cup brown or green lentils',
      '1 onion, diced',
      '2 carrots, diced',
      '2 celery stalks, diced',
      '4 cups vegetable stock',
      '1 tin chopped tomatoes, 1 tsp cumin, 1 bay leaf',
    ],
    steps: [
      'Sauté onion, carrot and celery in oil for 5 minutes.',
      'Add lentils, tomatoes, stock, cumin, bay leaf.',
      'Bring to a boil, then simmer 25–30 minutes until lentils are tender.',
      'Season with salt and pepper.',
      'Serve with a slice of wholemeal bread.',
    ],
    recovery_note:
      'Lentils deliver iron, folate and slow-burn carbs, which can ease the tiredness common in early Recovery. Makes 4 servings; freezes well.',
  },
  {
    id: 'l4',
    title: 'Grilled Chicken Caesar Wrap',
    category: 'lunch',
    image: img('1565299624946-b28f40a0ae38'),
    prep_minutes: 10, cook_minutes: 8, difficulty: 'easy',
    tags: ['high-protein', 'quick'],
    ingredients: [
      '1 chicken breast, sliced',
      '1 wholemeal wrap',
      '2 cups romaine lettuce, shredded',
      '2 tbsp light Caesar dressing',
      '2 tbsp parmesan, shaved',
      '1 tsp olive oil, salt, pepper',
    ],
    steps: [
      'Season chicken; grill 3–4 minutes per side until cooked through.',
      'Slice into strips.',
      'Toss lettuce with dressing.',
      'Pile chicken, lettuce and parmesan onto the wrap.',
      'Roll tightly, slice in half, serve.',
    ],
    recovery_note:
      'Lean chicken provides complete protein essential for liver repair and neurotransmitter synthesis after long-term substance use.',
  },
  {
    id: 'l5',
    title: 'Mediterranean Chickpea Salad',
    category: 'lunch',
    image: img('1540420773420-3366772f4999'),
    prep_minutes: 10, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'budget'],
    ingredients: [
      '1 tin chickpeas, drained',
      '1 cucumber, diced',
      '1 cup cherry tomatoes, halved',
      '50g feta, crumbled',
      '10 kalamata olives',
      'Olive oil, lemon juice, oregano',
    ],
    steps: [
      'Combine chickpeas, cucumber, tomatoes, olives in a bowl.',
      'Top with feta.',
      'Whisk olive oil, lemon juice, oregano; pour over.',
      'Toss gently.',
      'Serve immediately or chill 30 min for deeper flavour.',
    ],
    recovery_note:
      'The Mediterranean pattern is the most studied diet in mental-health research. It is repeatedly shown to lower depression and anxiety symptoms.',
  },
  {
    id: 'l6',
    title: 'Salmon Poke Bowl',
    category: 'lunch',
    image: img('1546069901-ba9599a7e63c'),
    prep_minutes: 15, cook_minutes: 15, difficulty: 'medium',
    tags: ['gluten-free', 'high-protein', 'omega-3'],
    ingredients: [
      '120g sashimi-grade salmon, diced',
      '1/2 cup brown rice, cooked',
      '1/4 avocado, sliced',
      '1/4 cup edamame',
      '1/4 cucumber, diced',
      'Soy sauce, sesame oil, sesame seeds',
    ],
    steps: [
      'Cook brown rice and let it cool slightly.',
      'Toss salmon with 1 tsp soy and sesame oil.',
      'Layer rice in a bowl.',
      'Arrange salmon, avocado, edamame, cucumber on top.',
      'Drizzle with soy and sprinkle sesame seeds.',
    ],
    recovery_note:
      'Omega-3 rich salmon plus complex carbs and fibre. This balanced meal keeps your energy steady and avoids the crash that can trigger cravings.',
  },
  {
    id: 'l7',
    title: 'Roasted Veggie & Hummus Wrap',
    category: 'lunch',
    image: img('1623428187969-5da2dcea5ebf'),
    prep_minutes: 10, cook_minutes: 20, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'budget'],
    ingredients: [
      '1 wholemeal wrap',
      '3 tbsp hummus',
      '1/2 zucchini, sliced',
      '1/2 capsicum, sliced',
      '1/2 red onion, sliced',
      'Spinach, olive oil, salt, pepper',
    ],
    steps: [
      'Toss vegetables with olive oil, salt, pepper.',
      'Roast at 200°C for 18–20 minutes until tender.',
      'Spread hummus on the wrap.',
      'Top with roasted veg and spinach.',
      'Roll tightly and slice.',
    ],
    recovery_note:
      'A fibre-rich plant-forward lunch that supports a healthy gut, which is linked to steadier mood in Recovery.',
  },
  {
    id: 'l8',
    title: 'Turkey & Avocado Sandwich',
    category: 'lunch',
    image: img('1539252554935-80c8cabf01ab'),
    prep_minutes: 5, cook_minutes: 0, difficulty: 'easy',
    tags: ['high-protein', 'quick'],
    ingredients: [
      '2 slices wholegrain bread',
      '4 slices roast turkey',
      '1/4 avocado, mashed',
      '1 lettuce leaf',
      '2 tomato slices',
      'Mustard, salt, pepper',
    ],
    steps: [
      'Spread mashed avocado on one slice of bread.',
      'Spread mustard on the other.',
      'Layer turkey, lettuce, tomato.',
      'Season; close the sandwich.',
      'Slice diagonally and serve.',
    ],
    recovery_note:
      'Turkey is rich in tryptophan, a serotonin precursor. Eaten alongside complex carbs, it supports calm focus through the afternoon.',
  },

  // ───────────── DINNER (8) ─────────────
  {
    id: 'd1',
    title: 'Baked Salmon with Roasted Vegetables',
    category: 'dinner',
    image: img('1467003909585-2f8a72700288'),
    prep_minutes: 10, cook_minutes: 25, difficulty: 'easy',
    tags: ['gluten-free', 'high-protein', 'omega-3'],
    ingredients: [
      '150g salmon fillet',
      '1 cup broccoli florets',
      '1 sweet potato, cubed',
      '1 lemon, sliced',
      '2 tbsp olive oil, herbs',
      'Salt, pepper',
    ],
    steps: [
      'Preheat oven to 200°C.',
      'Toss vegetables with olive oil, salt, pepper; spread on tray.',
      'Roast 10 minutes.',
      'Add salmon, top with lemon and herbs.',
      'Bake another 12–15 min until salmon is just cooked. Serve.',
    ],
    recovery_note:
      'A weekly serve of fatty fish is linked to better mood in several studies. It is a good habit in Recovery.',
  },
  {
    id: 'd2',
    title: 'Vegetable Stir-Fry with Tofu',
    category: 'dinner',
    image: img('1546069901-ba9599a7e63c'),
    prep_minutes: 15, cook_minutes: 10, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'high-protein'],
    ingredients: [
      '200g firm tofu, cubed',
      '2 cups mixed stir-fry veg (broccoli, capsicum, carrot)',
      '2 cloves garlic, minced',
      '1 tbsp ginger, grated',
      '2 tbsp soy sauce, 1 tsp sesame oil',
      'Cooked brown rice to serve',
    ],
    steps: [
      'Press tofu to remove water; cube.',
      'Pan-fry tofu in oil until golden on all sides; set aside.',
      'Stir-fry garlic and ginger 30 seconds.',
      'Add veg, stir-fry 4–5 minutes until tender-crisp.',
      'Return tofu, add soy and sesame oil. Serve over rice.',
    ],
    recovery_note:
      'Plant-based meals are naturally rich in B vitamins and antioxidants, which help protect the body from damage caused by long-term drinking or drug use.',
  },
  {
    id: 'd3',
    title: 'Chicken & Vegetable Stew',
    category: 'dinner',
    image: img('1547592180-85f173990554'),
    prep_minutes: 15, cook_minutes: 40, difficulty: 'medium',
    tags: ['gluten-free', 'high-protein', 'budget'],
    ingredients: [
      '2 chicken thighs, cubed',
      '1 onion, 2 carrots, 2 celery, diced',
      '2 potatoes, cubed',
      '4 cups chicken stock',
      'Bay leaf, thyme, salt, pepper',
      '1 tbsp olive oil',
    ],
    steps: [
      'Brown chicken in oil over medium-high heat; remove.',
      'Sauté onion, carrot, celery 5 minutes.',
      'Return chicken; add potatoes, stock, herbs.',
      'Simmer covered 30 minutes until chicken and potatoes are tender.',
      'Season and serve hot.',
    ],
    recovery_note:
      'Slow-cooked broths support gut lining repair — important since long-term alcohol use damages gut integrity, contributing to inflammation and mood issues.',
  },
  {
    id: 'd4',
    title: 'Vegetarian Chilli',
    category: 'dinner',
    image: img('1543339308-43e59d6b73a6'),
    prep_minutes: 10, cook_minutes: 30, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'budget', 'high-fibre'],
    ingredients: [
      '1 tin black beans',
      '1 tin kidney beans',
      '1 tin chopped tomatoes',
      '1 onion, 2 cloves garlic',
      '1 capsicum, diced',
      'Chilli powder, cumin, paprika, oregano',
    ],
    steps: [
      'Sauté onion, garlic and capsicum 5 minutes.',
      'Add spices; toast 30 seconds.',
      'Add tomatoes and beans (with their liquid).',
      'Simmer uncovered 25 minutes.',
      'Serve with rice or wholegrain tortillas, top with avocado.',
    ],
    recovery_note:
      'Beans are the cheapest health-food on the planet — packed with fibre, protein, magnesium and B-vitamins for steady mood and energy.',
  },
  {
    id: 'd5',
    title: 'Spaghetti Bolognese (Lean Beef)',
    category: 'dinner',
    image: img('1551183053-bf91a1d81141'),
    prep_minutes: 10, cook_minutes: 30, difficulty: 'easy',
    tags: ['high-protein', 'budget'],
    ingredients: [
      '300g lean beef mince',
      '1 onion, 2 cloves garlic, 1 carrot, finely diced',
      '1 tin chopped tomatoes',
      '2 tbsp tomato paste',
      'Italian herbs, salt, pepper',
      '300g wholemeal spaghetti',
    ],
    steps: [
      'Sauté onion, garlic, carrot 5 minutes.',
      'Add mince; brown well, breaking up.',
      'Stir in tomato paste; cook 1 minute.',
      'Add tomatoes and herbs. Simmer 20 minutes.',
      'Cook spaghetti per packet; drain. Serve sauce over pasta.',
    ],
    recovery_note:
      'Iron and zinc in beef are vital for energy, immunity and dopamine synthesis — often depleted after years of heavy drinking or drug use.',
  },
  {
    id: 'd6',
    title: 'Mushroom & Spinach Risotto',
    category: 'dinner',
    image: img('1476124369491-e7addf5db371'),
    prep_minutes: 10, cook_minutes: 25, difficulty: 'medium',
    tags: ['vegetarian'],
    ingredients: [
      '1 cup arborio rice',
      '300g mushrooms, sliced',
      '2 cups baby spinach',
      '1 onion, 2 cloves garlic',
      '4 cups warm vegetable stock',
      'Parmesan, butter, olive oil',
    ],
    steps: [
      'Sauté onion, garlic, mushrooms 5 minutes.',
      'Add rice; stir to coat 1 minute.',
      'Add stock one ladle at a time, stirring, until absorbed (20 min).',
      'Stir in spinach until wilted.',
      'Finish with butter and parmesan.',
    ],
    recovery_note:
      'A grounding, nourishing meal. Mushrooms add B-vitamins and selenium; the slow stirring itself becomes a meditative ritual.',
  },
  {
    id: 'd7',
    title: 'Grilled Chicken with Quinoa & Greens',
    category: 'dinner',
    image: img('1532550907401-a500c9a57435'),
    prep_minutes: 10, cook_minutes: 20, difficulty: 'easy',
    tags: ['gluten-free', 'high-protein'],
    ingredients: [
      '1 chicken breast',
      '1/2 cup quinoa',
      '2 cups mixed greens',
      '1 lemon, juiced',
      'Olive oil, paprika, salt, pepper',
      'Hummus or yoghurt sauce to serve',
    ],
    steps: [
      'Cook quinoa per packet.',
      'Season chicken with paprika, salt, pepper, olive oil.',
      'Grill 4–5 min per side until cooked through.',
      'Slice. Plate quinoa, greens, chicken.',
      'Squeeze lemon, drizzle olive oil, add a spoon of hummus.',
    ],
    recovery_note:
      'Complete protein, complex carbs, leafy greens — a clean dinner-plate template you can repeat hundreds of ways without boredom.',
  },
  {
    id: 'd8',
    title: 'Baked Cod with Lemon & Herbs',
    category: 'dinner',
    image: img('1485921325833-c519f76c4927'),
    prep_minutes: 5, cook_minutes: 18, difficulty: 'easy',
    tags: ['gluten-free', 'high-protein', 'quick'],
    ingredients: [
      '150g cod or any white fish fillet',
      '1 lemon, sliced',
      'Fresh dill, parsley',
      '2 tbsp olive oil',
      'Salt, pepper',
      'Steamed greens to serve',
    ],
    steps: [
      'Preheat oven to 200°C.',
      'Place cod in a baking dish, top with lemon, herbs, oil, salt, pepper.',
      'Cover with foil; bake 12–15 minutes until flaky.',
      'Serve with steamed greens or salad.',
      'Spoon pan juices over.',
    ],
    recovery_note:
      'Lean white fish delivers protein and selenium with minimal saturated fat — gentle on the recovering liver and easy to digest.',
  },

  // ───────────── SNACKS (4) ─────────────
  {
    id: 's1',
    title: 'Apple & Almond Butter',
    category: 'snacks',
    image: img('1568702846914-96b305d2aaeb'),
    prep_minutes: 2, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'quick'],
    ingredients: [
      '1 apple, sliced',
      '2 tbsp almond butter',
      'Pinch of cinnamon (optional)',
    ],
    steps: [
      'Slice the apple into wedges.',
      'Sprinkle with cinnamon.',
      'Serve with almond butter for dipping.',
    ],
    recovery_note:
      'A simple snack that combines fibre, healthy fat and a touch of natural sugar — perfect for mid-afternoon energy slumps.',
  },
  {
    id: 's2',
    title: 'Hummus with Veggie Sticks',
    category: 'snacks',
    image: img('1568625365131-079e026a927d'),
    prep_minutes: 5, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'budget'],
    ingredients: [
      '1/2 cup hummus',
      '1 carrot, cut into sticks',
      '1 celery stalk, cut into sticks',
      '1/2 cucumber, sliced',
      '1/2 capsicum, sliced',
    ],
    steps: [
      'Cut all vegetables into sticks.',
      'Spoon hummus into a small bowl.',
      'Arrange veggies around hummus and serve.',
    ],
    recovery_note:
      'Crunchy, satisfying and packed with fibre — replaces salty packet snacks that drive you toward salt + sugar binge cycles.',
  },
  {
    id: 's3',
    title: 'Greek Yoghurt with Honey & Walnuts',
    category: 'snacks',
    image: img('1488477181946-6428a0291777'),
    prep_minutes: 2, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'high-protein', 'quick'],
    ingredients: [
      '3/4 cup plain Greek yoghurt',
      '1 tsp honey',
      '2 tbsp walnuts, chopped',
      'Pinch of cinnamon',
    ],
    steps: [
      'Spoon yoghurt into a bowl.',
      'Drizzle with honey.',
      'Top with walnuts and cinnamon.',
    ],
    recovery_note:
      'Protein + healthy fats keep you full longer than carb-heavy snacks. Walnuts deliver the only plant source of significant ALA omega-3.',
  },
  {
    id: 's4',
    title: 'Trail Mix Handful',
    category: 'snacks',
    image: img('1606312619070-d48b4c652a52'),
    prep_minutes: 2, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'budget', 'quick'],
    ingredients: [
      '2 tbsp raw almonds',
      '2 tbsp pumpkin seeds',
      '1 tbsp dried cranberries',
      '1 tbsp dark chocolate chips',
    ],
    steps: [
      'Combine all ingredients.',
      'Portion into 30g servings.',
      'Store in an airtight jar; grab when needed.',
    ],
    recovery_note:
      'Magnesium-rich seeds support nervous-system calm. The dark chocolate adds a tiny dopamine reward without the crash of refined sugar.',
  },

  // ───────────── SMOOTHIES (4) ─────────────
  {
    id: 'sm1',
    title: 'Berry Recovery Smoothie',
    category: 'smoothies',
    image: img('1502741338009-cac2772e18bc'),
    prep_minutes: 5, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'gluten-free', 'high-protein', 'quick'],
    ingredients: [
      '1 cup mixed berries (frozen)',
      '1 banana',
      '1 cup unsweetened milk or oat milk',
      '1 scoop vanilla protein powder (optional)',
      '1 tbsp chia seeds',
      'Ice as needed',
    ],
    steps: [
      'Add all ingredients to blender.',
      'Blend on high 30–45 seconds until smooth.',
      'Pour into a glass; serve cold.',
    ],
    recovery_note:
      'Antioxidant-rich berries plus chia omega-3s — a brain-supportive way to start the day or rebound after a workout.',
  },
  {
    id: 'sm2',
    title: 'Green Detox Smoothie',
    category: 'smoothies',
    image: img('1610970878459-69b6cdc9b50b'),
    prep_minutes: 5, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'quick'],
    ingredients: [
      '1 cup baby spinach',
      '1/2 cucumber',
      '1 green apple, cored',
      '1 tbsp lemon juice',
      '1-inch fresh ginger',
      '1 cup coconut water',
    ],
    steps: [
      'Add all ingredients to blender.',
      'Blend until completely smooth.',
      'Strain if desired and serve over ice.',
    ],
    recovery_note:
      'Hydrating and gently detoxifying — supports liver function as it rebuilds after the metabolic load of long-term substance use.',
  },
  {
    id: 'sm3',
    title: 'Chocolate Banana Protein Shake',
    category: 'smoothies',
    image: img('1571091718767-18b5b1457add'),
    prep_minutes: 5, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'high-protein', 'quick'],
    ingredients: [
      '1 banana',
      '1 tbsp cocoa powder (unsweetened)',
      '1 scoop chocolate protein powder',
      '1 cup milk of choice',
      '1 tbsp peanut butter',
      'Ice',
    ],
    steps: [
      'Combine all ingredients in a blender.',
      'Blend on high until thick and creamy.',
      'Pour into a glass and enjoy immediately.',
    ],
    recovery_note:
      'Cocoa is rich in flavanols and contains theobromine — a gentle, non-addictive mood lifter. Ideal post-meeting or post-workout reward.',
  },
  {
    id: 'sm4',
    title: 'Tropical Sunrise Smoothie',
    category: 'smoothies',
    image: img('1623428187969-5da2dcea5ebf'),
    prep_minutes: 5, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'quick'],
    ingredients: [
      '1 cup frozen mango',
      '1/2 cup frozen pineapple',
      '1 banana',
      '1 cup orange juice or coconut water',
      '1 tbsp ground flaxseed',
    ],
    steps: [
      'Add all ingredients to blender.',
      'Blend until silky smooth.',
      'Pour into a glass and serve.',
    ],
    recovery_note:
      'Vitamin-C powerhouse to support adrenal recovery — useful in the first months when stress response is recalibrating without alcohol.',
  },

  // ───────────── ADDITIONAL LUNCHES (8) ─────────────
  {
    id: 'l9',
    title: 'Tomato Basil Soup with Grilled Cheese',
    category: 'lunch',
    image: img('1547308283-b941a571d40b'),
    prep_minutes: 10, cook_minutes: 25, difficulty: 'easy',
    tags: ['vegetarian', 'budget'],
    ingredients: [
      '1 tin chopped tomatoes',
      '1 onion, diced',
      '2 cloves garlic',
      '1 cup vegetable stock',
      '1/2 cup fresh basil',
      '2 slices wholegrain bread, cheddar, butter',
    ],
    steps: [
      'Sauté onion and garlic 5 minutes.',
      'Add tomatoes, stock, basil; simmer 15 min.',
      'Blend until smooth; season.',
      'Butter bread, fill with cheese, grill in pan until golden both sides.',
      'Serve sandwich alongside soup.',
    ],
    recovery_note:
      'Comfort food without the binge — lycopene from tomatoes supports brain health, calcium from cheese aids nervous system stability.',
  },
  {
    id: 'l10',
    title: 'Egg Salad Sandwich',
    category: 'lunch',
    image: img('1528735602780-2552fd46c7af'),
    prep_minutes: 10, cook_minutes: 10, difficulty: 'easy',
    tags: ['vegetarian', 'high-protein', 'budget', 'quick'],
    ingredients: [
      '3 hard-boiled eggs',
      '2 tbsp Greek yoghurt',
      '1 tsp Dijon mustard',
      '1 tbsp chives, chopped',
      '2 slices wholegrain bread',
      'Lettuce, salt, pepper',
    ],
    steps: [
      'Boil eggs 8 minutes; cool and peel.',
      'Mash with yoghurt, mustard, chives, salt, pepper.',
      'Layer onto bread with lettuce.',
      'Close, slice and serve.',
      'Pairs well with crunchy veg sticks.',
    ],
    recovery_note:
      'Eggs deliver choline and B12, both of which are commonly depleted in long-term substance use and essential for cognitive recovery.',
  },
  {
    id: 'l11',
    title: 'Asian Chicken Lettuce Cups',
    category: 'lunch',
    image: img('1606756790138-261d2b21cd77'),
    prep_minutes: 15, cook_minutes: 12, difficulty: 'medium',
    tags: ['gluten-free', 'high-protein'],
    ingredients: [
      '300g chicken mince',
      '1 tbsp ginger, grated',
      '2 cloves garlic, minced',
      '2 tbsp soy sauce, 1 tsp honey',
      '1 carrot, grated',
      'Iceberg lettuce leaves, sesame seeds',
    ],
    steps: [
      'Brown chicken mince in pan 5 min.',
      'Add garlic, ginger; cook 1 min.',
      'Stir in soy, honey, carrot; cook 3 more min.',
      'Spoon into lettuce cups.',
      'Top with sesame seeds; serve.',
    ],
    recovery_note:
      'Lean protein with a gentle dopamine boost from the umami-savoury profile — satisfying without crashing your blood sugar.',
  },
  {
    id: 'l12',
    title: 'Black Bean & Corn Salad',
    category: 'lunch',
    image: img('1604908554027-bb6cb3aa64ea'),
    prep_minutes: 10, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'budget', 'quick'],
    ingredients: [
      '1 tin black beans, rinsed',
      '1 cup corn (frozen, thawed)',
      '1 capsicum, diced',
      '1/4 red onion, finely chopped',
      '1 lime, juiced',
      'Olive oil, coriander, cumin, salt',
    ],
    steps: [
      'Combine beans, corn, capsicum, onion in a bowl.',
      'Whisk lime juice, olive oil, cumin and salt.',
      'Pour over salad; toss.',
      'Top with fresh coriander.',
      'Serve immediately or chill for 1 hour.',
    ],
    recovery_note:
      'Plant-based protein and fibre stabilise blood sugar through the afternoon. The Mexican flavour profile is exciting without being stimulating.',
  },
  {
    id: 'l13',
    title: 'Sweet Potato & Black Bean Tacos',
    category: 'lunch',
    image: img('1551504734-5ee1c4a1479b'),
    prep_minutes: 10, cook_minutes: 25, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'budget'],
    ingredients: [
      '1 large sweet potato, cubed',
      '1 tin black beans, drained',
      '1 tsp cumin, paprika, chilli',
      '4 small corn tortillas',
      '1/4 avocado, sliced',
      'Lime, coriander, salsa',
    ],
    steps: [
      'Toss sweet potato with oil, spices; roast 200°C for 20 min.',
      'Warm beans with extra spices for 3 min.',
      'Heat tortillas in a dry pan.',
      'Layer sweet potato, beans, avocado in tortillas.',
      'Top with lime, coriander, salsa.',
    ],
    recovery_note:
      'Sweet potato delivers gentle, sustained carbohydrate energy plus beta-carotene — both protective for the recovering nervous system.',
  },
  {
    id: 'l14',
    title: 'Greek Salad with Grilled Chicken',
    category: 'lunch',
    image: img('1546069901-ba9599a7e63c'),
    prep_minutes: 10, cook_minutes: 10, difficulty: 'easy',
    tags: ['gluten-free', 'high-protein'],
    ingredients: [
      '1 chicken breast',
      '1 cucumber, chunked',
      '2 tomatoes, chunked',
      '1/4 red onion, sliced',
      '50g feta, kalamata olives',
      'Olive oil, lemon, oregano',
    ],
    steps: [
      'Season chicken; grill 4 min per side. Slice.',
      'Combine cucumber, tomatoes, onion, olives in a bowl.',
      'Top with feta, then sliced chicken.',
      'Whisk olive oil, lemon, oregano; pour over.',
      'Toss gently and serve.',
    ],
    recovery_note:
      'A textbook Mediterranean lunch — lean protein, bright vegetables, healthy fats. This eating pattern has the strongest evidence for mental health support.',
  },
  {
    id: 'l15',
    title: 'Pumpkin & Lentil Curry',
    category: 'lunch',
    image: img('1565557623262-b51c2513a641'),
    prep_minutes: 10, cook_minutes: 30, difficulty: 'medium',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'budget'],
    ingredients: [
      '1 cup red lentils',
      '2 cups pumpkin, cubed',
      '1 onion, garlic, ginger',
      '1 tin coconut milk',
      '2 tsp curry powder, turmeric',
      'Spinach, lime, basmati rice',
    ],
    steps: [
      'Sauté onion, garlic, ginger 3 min.',
      'Add spices; cook 30 sec.',
      'Add lentils, pumpkin, coconut milk + 1 cup water.',
      'Simmer 25 min until lentils break down and pumpkin is tender.',
      'Stir in spinach; serve with rice and lime.',
    ],
    recovery_note:
      'Anti-inflammatory turmeric, B-vitamin rich lentils, beta-carotene from pumpkin — a brain-loving bowl that feels indulgent but heals.',
  },
  {
    id: 'l16',
    title: 'Caprese & Prosciutto Plate',
    category: 'lunch',
    image: img('1572441713132-51c75654db73'),
    prep_minutes: 10, cook_minutes: 0, difficulty: 'easy',
    tags: ['gluten-free', 'high-protein', 'quick'],
    ingredients: [
      '1 ball fresh mozzarella, sliced',
      '2 tomatoes, sliced',
      '60g prosciutto',
      'Fresh basil leaves',
      'Balsamic glaze, olive oil',
      'Crusty wholegrain bread',
    ],
    steps: [
      'Alternate tomato and mozzarella slices on a plate.',
      'Drape prosciutto in folds across the plate.',
      'Tuck basil leaves between slices.',
      'Drizzle with olive oil and balsamic glaze.',
      'Season; serve with bread.',
    ],
    recovery_note:
      'A 10-minute Italian-inspired plate. Restaurant-quality food without the temptation of restaurant alcohol. Protein, calcium and antioxidants in one beautiful plate.',
  },

  // ───────────── ADDITIONAL DINNERS (10) ─────────────
  {
    id: 'd9',
    title: 'Beef & Vegetable Stir-Fry',
    category: 'dinner',
    image: img('1582450871972-95dd6e5e0c92'),
    prep_minutes: 15, cook_minutes: 12, difficulty: 'medium',
    tags: ['high-protein', 'gluten-free'],
    ingredients: [
      '300g lean beef strips',
      '1 tbsp ginger, 2 cloves garlic',
      '1 capsicum, 1 broccoli head, 1 carrot',
      '3 tbsp soy sauce, 1 tbsp oyster sauce',
      '1 tsp sesame oil, cornflour',
      'Cooked brown rice',
    ],
    steps: [
      'Toss beef with cornflour and 1 tbsp soy.',
      'Stir-fry beef in hot oil 2 min; remove.',
      'Stir-fry garlic, ginger, vegetables 4 min.',
      'Return beef, add remaining sauces; toss 1 min.',
      'Serve over brown rice with sesame oil drizzle.',
    ],
    recovery_note:
      'Iron and zinc in lean beef are commonly depleted after long-term alcohol use — both critical for energy, mood and immune recovery.',
  },
  {
    id: 'd10',
    title: 'Roast Chicken with Root Vegetables',
    category: 'dinner',
    image: img('1598103442097-8b74394b95c6'),
    prep_minutes: 15, cook_minutes: 60, difficulty: 'easy',
    tags: ['gluten-free', 'high-protein', 'budget'],
    ingredients: [
      '1.4kg whole chicken',
      '4 carrots, 2 parsnips, 1 onion, chunks',
      '6 garlic cloves, unpeeled',
      'Lemon, fresh herbs (rosemary, thyme)',
      '3 tbsp olive oil',
      'Salt, pepper',
    ],
    steps: [
      'Preheat oven to 200°C.',
      'Season chicken inside and out; stuff with lemon and herbs.',
      'Toss vegetables with oil, salt, pepper; spread in a roasting pan.',
      'Place chicken on top; roast 50-60 min until juices run clear.',
      'Rest 10 min; carve and serve.',
    ],
    recovery_note:
      'Sunday-roast comfort that feeds you for two days. Family-style cooking restores the routine and ritual that addiction strips away.',
  },
  {
    id: 'd11',
    title: 'Vegetable Lasagne',
    category: 'dinner',
    image: img('1574894709920-11b28e7367e3'),
    prep_minutes: 25, cook_minutes: 45, difficulty: 'medium',
    tags: ['vegetarian', 'budget'],
    ingredients: [
      '1 zucchini, 1 eggplant, sliced',
      '1 jar marinara sauce',
      '250g ricotta, 1 egg',
      '8 lasagne sheets',
      '1.5 cups grated mozzarella',
      'Spinach, basil, parmesan',
    ],
    steps: [
      'Roast eggplant and zucchini 15 min at 200°C.',
      'Mix ricotta, egg, spinach, basil.',
      'Layer in dish: sauce, sheets, ricotta, veg, mozzarella; repeat.',
      'Top with parmesan.',
      'Bake covered 30 min; uncovered 10 min until golden.',
    ],
    recovery_note:
      'Meal-prep gold. Makes 6 servings — protected dinners for a week reduce decision fatigue, a known relapse trigger.',
  },
  {
    id: 'd12',
    title: 'Thai Green Curry with Vegetables',
    category: 'dinner',
    image: img('1455619452474-d2be8b1e70cd'),
    prep_minutes: 15, cook_minutes: 20, difficulty: 'medium',
    tags: ['vegetarian', 'vegan', 'gluten-free'],
    ingredients: [
      '2 tbsp green curry paste',
      '1 tin coconut milk',
      '1 cup mixed vegetables (broccoli, capsicum, snow peas)',
      '200g firm tofu, cubed',
      '1 tbsp fish sauce (or soy for vegan)',
      'Basil, lime, jasmine rice',
    ],
    steps: [
      'Fry curry paste in 1 tbsp oil 1 min.',
      'Add coconut milk and bring to gentle simmer.',
      'Add tofu and vegetables; cook 8 min.',
      'Stir in fish sauce and squeeze of lime.',
      'Serve over jasmine rice; top with basil.',
    ],
    recovery_note:
      'Coconut MCTs provide immediate brain energy. Spicy food gives endorphin kicks without dependency — a healthy hedonic upgrade.',
  },
  {
    id: 'd13',
    title: 'Salmon Teriyaki Bowl',
    category: 'dinner',
    image: img('1467003909585-2f8a72700288'),
    prep_minutes: 10, cook_minutes: 15, difficulty: 'easy',
    tags: ['gluten-free', 'high-protein', 'omega-3'],
    ingredients: [
      '2 salmon fillets',
      '3 tbsp soy sauce, 1 tbsp honey',
      '1 tsp ginger, grated',
      '1 cup brown rice',
      'Edamame, carrots, cucumber',
      'Sesame seeds, spring onion',
    ],
    steps: [
      'Whisk soy, honey, ginger; brush over salmon.',
      'Bake at 200°C for 12-14 min until flaky.',
      'Cook rice per packet.',
      'Arrange rice in bowls; top with salmon and vegetables.',
      'Garnish with sesame seeds and spring onion.',
    ],
    recovery_note:
      'Omega-3 EPA & DHA from salmon support depression treatment with strong clinical evidence — one of the highest-leverage meals you can build a week around.',
  },
  {
    id: 'd14',
    title: 'Stuffed Capsicums',
    category: 'dinner',
    image: img('1605791770440-cb8e64f88a36'),
    prep_minutes: 15, cook_minutes: 35, difficulty: 'medium',
    tags: ['gluten-free', 'high-protein'],
    ingredients: [
      '4 large capsicums',
      '300g lean beef or turkey mince',
      '1 cup cooked brown rice',
      '1 onion, garlic, 1 tin tomatoes',
      '1 cup grated cheese',
      'Italian herbs',
    ],
    steps: [
      'Halve capsicums and remove seeds.',
      'Brown mince with onion and garlic.',
      'Stir in rice, tomatoes, herbs.',
      'Spoon mixture into capsicum halves; top with cheese.',
      'Bake at 180°C for 30 min until tender and golden.',
    ],
    recovery_note:
      'Vitamin C in capsicums supports adrenal recovery — your stress system needs serious rebuilding in the first year of sobriety.',
  },
  {
    id: 'd15',
    title: 'Mushroom Stroganoff',
    category: 'dinner',
    image: img('1476124369491-e7addf5db371'),
    prep_minutes: 10, cook_minutes: 25, difficulty: 'medium',
    tags: ['vegetarian'],
    ingredients: [
      '500g mixed mushrooms, sliced',
      '1 onion, 2 cloves garlic',
      '1 cup vegetable stock',
      '1/2 cup sour cream or yoghurt',
      '1 tbsp Dijon mustard, paprika',
      'Wholemeal pasta or rice',
    ],
    steps: [
      'Sauté onion 3 min, add garlic and mushrooms; cook 8 min.',
      'Stir in mustard, paprika, stock; simmer 8 min.',
      'Remove from heat; stir in sour cream.',
      'Cook pasta per packet.',
      'Serve sauce over pasta with parsley.',
    ],
    recovery_note:
      'Mushrooms are nature\'s vitamin D pharmacy — vital for the immune system and mood, often deficient in long-term substance use.',
  },
  {
    id: 'd16',
    title: 'Slow-Cooker Chicken Tikka',
    category: 'dinner',
    image: img('1565557623262-b51c2513a641'),
    prep_minutes: 15, cook_minutes: 240, difficulty: 'easy',
    tags: ['gluten-free', 'high-protein'],
    ingredients: [
      '600g chicken thigh, cubed',
      '1 cup Greek yoghurt',
      '3 tbsp tikka paste',
      '1 onion, garlic, ginger',
      '1 tin chopped tomatoes',
      'Basmati rice, naan, fresh coriander',
    ],
    steps: [
      'Marinate chicken in yoghurt and tikka paste 30 min.',
      'Layer onion, garlic, ginger in slow cooker; add chicken and tomatoes.',
      'Cook on low 4 hours.',
      'Stir well; check seasoning.',
      'Serve with rice, naan, coriander.',
    ],
    recovery_note:
      'Set-and-forget meals are critical for sober evenings — pre-committed dinners reduce the unstructured time when cravings hit hardest.',
  },
  {
    id: 'd17',
    title: 'Zucchini Noodle Bolognese',
    category: 'dinner',
    image: img('1551183053-bf91a1d81141'),
    prep_minutes: 15, cook_minutes: 25, difficulty: 'easy',
    tags: ['gluten-free', 'high-protein', 'low-carb'],
    ingredients: [
      '300g lean turkey or beef mince',
      '4 medium zucchinis, spiralised',
      '1 jar marinara sauce',
      '1 onion, garlic, carrot',
      'Italian herbs, parmesan',
      'Olive oil, salt, pepper',
    ],
    steps: [
      'Sauté onion, garlic, carrot 5 min.',
      'Add mince; brown 5 min.',
      'Stir in marinara and herbs; simmer 12 min.',
      'Sauté zucchini noodles in oil 2 min.',
      'Top noodles with sauce and parmesan.',
    ],
    recovery_note:
      'Lower glycaemic load means steadier mood and energy through the evening — fewer chances for sugar crashes that mimic cravings.',
  },
  {
    id: 'd18',
    title: 'Grilled Halloumi & Quinoa Salad',
    category: 'dinner',
    image: img('1540420773420-3366772f4999'),
    prep_minutes: 10, cook_minutes: 12, difficulty: 'easy',
    tags: ['vegetarian', 'gluten-free', 'high-protein'],
    ingredients: [
      '200g halloumi, sliced',
      '1 cup cooked quinoa',
      '2 cups mixed leaves',
      '1 cup cherry tomatoes',
      '1 cucumber, mint',
      'Lemon, olive oil, pomegranate seeds',
    ],
    steps: [
      'Grill halloumi in dry pan 1 min per side until golden.',
      'Toss quinoa, leaves, tomatoes, cucumber, mint.',
      'Whisk lemon juice and olive oil.',
      'Plate salad, top with halloumi.',
      'Sprinkle pomegranate seeds; serve.',
    ],
    recovery_note:
      'A vegetarian dinner that delivers serious protein and feels celebratory — proof that "healthy" doesn\'t mean "boring" in Recovery.',
  },

  // ───────────── ADDITIONAL BREAKFAST + SNACK ─────────────
  {
    id: 'b7',
    title: 'Overnight Chia Pudding',
    category: 'breakfast',
    image: img('1502741338009-cac2772e18bc'),
    prep_minutes: 5, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'high-fibre'],
    ingredients: [
      '3 tbsp chia seeds',
      '1 cup unsweetened almond or oat milk',
      '1 tsp maple syrup',
      '1/2 tsp vanilla extract',
      '1/2 cup mixed berries',
      '1 tbsp flaked almonds',
    ],
    steps: [
      'Whisk chia, milk, maple syrup, vanilla in a jar.',
      'Stir again after 5 min to break up clumps.',
      'Refrigerate overnight (or at least 4 hours).',
      'Top with berries and almonds.',
      'Eat straight from the jar.',
    ],
    recovery_note:
      'Make-ahead breakfast removes morning decision-fatigue. Chia delivers omega-3 and fibre that keep you full and stable until lunch.',
  },
  {
    id: 's5',
    title: 'Banana with Peanut Butter & Dark Chocolate',
    category: 'snacks',
    image: img('1568702846914-96b305d2aaeb'),
    prep_minutes: 3, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'gluten-free', 'quick'],
    ingredients: [
      '1 ripe banana',
      '1 tbsp peanut butter',
      '1 tsp dark chocolate chips (70%+)',
      'Pinch of cinnamon',
    ],
    steps: [
      'Slice banana lengthwise.',
      'Spread peanut butter on top.',
      'Sprinkle chocolate chips and cinnamon.',
      'Serve immediately or wrap and chill 10 min for a firmer texture.',
    ],
    recovery_note:
      'A satisfying treat that hits the sweet/salty/fat reward circuits without sugar overload — perfect for the 4pm urge that often signalled drinks o\'clock.',
  },
];

// Default country='western' for the original 50 meals that don't specify it
DIET_MEALS.forEach(m => { if (!m.country) m.country = 'western'; });

// Verify counts: 7 breakfast + 16 lunch + 18 dinner + 5 snacks + 4 smoothies = 50 western meals + 70 international = 120 total
// International countries: russia, ukraine, portugal, france, italy, spain, korea (10 each, all alcohol-free)
DIET_MEALS.push(
  // ───────────── RUSSIA (10) ─────────────
  { id: 'ru1', title: 'Borscht', country: 'russia', category: 'lunch', image: img('1547592180-85f173990554'), prep_minutes: 15, cook_minutes: 45, difficulty: 'easy',
    tags: ['vegetarian', 'budget', 'high-fibre'],
    ingredients: ['3 medium beetroot, peeled and grated', '1 onion, 2 carrots, 2 potatoes, diced', '1/4 small cabbage, shredded', '1 tbsp tomato paste', '6 cups vegetable stock', 'Sour cream, dill, garlic'],
    steps: ['Sauté onion and carrot 5 min.', 'Add beetroot, potatoes, cabbage and stock.', 'Stir in tomato paste; simmer 35 min until vegetables are tender.', 'Season with salt, pepper, garlic.', 'Serve with sour cream and dill.'],
    recovery_note: 'Beetroot supports liver detoxification — vital in early Recovery as the body clears the metabolic burden of long-term substance use.' },
  { id: 'ru2', title: 'Beef Stroganoff', country: 'russia', category: 'dinner', image: img('1551183053-bf91a1d81141'), prep_minutes: 10, cook_minutes: 20, difficulty: 'easy',
    tags: ['high-protein'],
    ingredients: ['400g lean beef strips', '300g mushrooms, sliced', '1 onion, sliced', '3/4 cup sour cream', '1 tbsp Dijon mustard', 'Wholemeal pasta or buckwheat'],
    steps: ['Brown beef in oil 2 min; remove.', 'Sauté onion and mushrooms 6 min.', 'Return beef; add mustard and stock if needed.', 'Stir in sour cream off heat.', 'Serve over pasta or buckwheat.'],
    recovery_note: 'Iron-rich beef plus comforting cream — restores energy without the empty calories of restaurant versions.' },
  { id: 'ru3', title: 'Buckwheat Kasha with Mushrooms', country: 'russia', category: 'lunch', image: img('1547308283-b941a571d40b'), prep_minutes: 5, cook_minutes: 25, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'budget'],
    ingredients: ['1 cup buckwheat groats', '300g mushrooms, sliced', '1 onion, diced', '2 cups water', '2 tbsp butter or olive oil', 'Fresh dill, salt, pepper'],
    steps: ['Toast buckwheat in dry pan 2 min.', 'Add water; simmer covered 18 min until tender.', 'Sauté onion and mushrooms in butter 8 min.', 'Combine buckwheat with mushroom mixture.', 'Top with dill; serve.'],
    recovery_note: 'Buckwheat is a complete protein and rich in magnesium — a mineral commonly depleted in chronic alcohol use and essential for nervous-system calm.' },
  { id: 'ru4', title: 'Olivier Salad', country: 'russia', category: 'lunch', image: img('1540420773420-3366772f4999'), prep_minutes: 20, cook_minutes: 15, difficulty: 'easy',
    tags: ['high-protein', 'budget'],
    ingredients: ['2 boiled potatoes, diced', '2 boiled carrots, diced', '4 boiled eggs, diced', '200g cooked chicken or ham, diced', '1 cup peas', '1/2 cup pickles, light mayo, dill'],
    steps: ['Boil potatoes, carrots and eggs separately; cool and dice.', 'Combine all ingredients in a large bowl.', 'Stir in mayo and pickles.', 'Season with salt, pepper, dill.', 'Chill 30 min before serving.'],
    recovery_note: 'Hearty, protein-dense and fits a sober celebration table — a Russian classic without the traditional vodka chaser.' },
  { id: 'ru5', title: 'Syrniki (Cottage Cheese Pancakes)', country: 'russia', category: 'breakfast', image: img('1528207776546-365bb710ee93'), prep_minutes: 10, cook_minutes: 10, difficulty: 'easy',
    tags: ['vegetarian', 'high-protein'],
    ingredients: ['250g cottage cheese', '1 egg', '3 tbsp flour', '1 tsp vanilla', '1 tbsp sugar (optional)', 'Sour cream and jam to serve'],
    steps: ['Mash cottage cheese with egg, flour, vanilla, sugar.', 'Form small patties.', 'Fry in butter 2 min per side.', 'Drain on paper.', 'Serve with sour cream and a spoon of jam.'],
    recovery_note: 'High-protein breakfast that anchors blood sugar for hours — particularly useful in early Recovery when energy crashes mimic cravings.' },
  { id: 'ru6', title: 'Shchi (Cabbage Soup)', country: 'russia', category: 'lunch', image: img('1547308283-b941a571d40b'), prep_minutes: 10, cook_minutes: 35, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'budget', 'high-fibre'],
    ingredients: ['1/2 small cabbage, shredded', '1 onion, 2 carrots, 1 potato, diced', '1 tin tomatoes', '6 cups vegetable stock', 'Bay leaf, dill, garlic'],
    steps: ['Sauté onion and carrot 5 min.', 'Add cabbage, potato, tomatoes, stock, bay leaf.', 'Simmer 25-30 min until cabbage is tender.', 'Stir in fresh garlic and dill.', 'Serve with rye bread.'],
    recovery_note: 'Cabbage delivers vitamin C and gut-supporting fibre — both crucial for immune and digestive recovery.' },
  { id: 'ru7', title: 'Plov (Rice Pilaf with Chicken)', country: 'russia', category: 'dinner', image: img('1546069901-ba9599a7e63c'), prep_minutes: 10, cook_minutes: 35, difficulty: 'easy',
    tags: ['high-protein', 'gluten-free', 'budget'],
    ingredients: ['400g chicken thigh, cubed', '1.5 cups long-grain rice', '2 carrots, julienned', '1 onion, sliced', '3 cups water', '4 garlic cloves, cumin, salt'],
    steps: ['Brown chicken 5 min; add onion and carrot.', 'Stir in rice and cumin; toast 1 min.', 'Add water and garlic cloves whole.', 'Simmer covered 25 min until rice is tender.', 'Rest 10 min; fluff and serve.'],
    recovery_note: 'A one-pot meal that builds the cooking confidence many people lose during active addiction — practical, satisfying, repeatable.' },
  { id: 'ru8', title: 'Cucumber & Dill Salad', country: 'russia', category: 'snacks', image: img('1568625365131-079e026a927d'), prep_minutes: 5, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'gluten-free', 'quick'],
    ingredients: ['2 cucumbers, sliced', '2 tbsp sour cream or Greek yoghurt', '2 tbsp fresh dill, chopped', '1 garlic clove, minced', 'Salt, pepper'],
    steps: ['Slice cucumbers thinly.', 'Combine with sour cream, dill, garlic.', 'Season; toss gently.', 'Chill 10 min before serving.'],
    recovery_note: 'Hydrating and refreshing — dehydration is a hidden trigger for cravings and fatigue in early sobriety.' },
  { id: 'ru9', title: 'Russian Cabbage & Mushroom Filling Wraps', country: 'russia', category: 'lunch', image: img('1623428187969-5da2dcea5ebf'), prep_minutes: 10, cook_minutes: 20, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'budget'],
    ingredients: ['4 large cabbage leaves', '300g mushrooms, finely chopped', '1 onion, diced', '1/2 cup cooked rice', 'Dill, salt, pepper, olive oil'],
    steps: ['Blanch cabbage leaves 2 min until pliable.', 'Sauté mushrooms and onion 8 min.', 'Mix with rice and dill.', 'Spoon filling onto leaves and roll.', 'Serve cold or warmed; pairs with sour cream.'],
    recovery_note: 'A vegan version of holubtsi — light, nourishing, and quick to assemble for busy weeknights.' },
  { id: 'ru10', title: 'Russian Berry Compote', country: 'russia', category: 'smoothies', image: img('1502741338009-cac2772e18bc'), prep_minutes: 5, cook_minutes: 15, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free'],
    ingredients: ['2 cups mixed berries (frozen or fresh)', '4 cups water', '2 tbsp honey or sugar', '1 cinnamon stick', '1 star anise (optional)'],
    steps: ['Combine berries, water, sweetener and spices in a pot.', 'Simmer 15 min.', 'Strain (or leave berries in).', 'Serve hot in winter or cold in summer.', 'Stays good 5 days in fridge.'],
    recovery_note: 'A traditional alcohol-free hot drink to replace the cultural ritual of nightly drinking with something warming and antioxidant-rich.' },

  // ───────────── UKRAINE (10) ─────────────
  { id: 'ua1', title: 'Ukrainian Borscht with Beans', country: 'ukraine', category: 'lunch', image: img('1547592180-85f173990554'), prep_minutes: 15, cook_minutes: 50, difficulty: 'easy',
    tags: ['vegetarian', 'budget', 'high-fibre'],
    ingredients: ['3 beetroot, grated', '1 tin white beans', '1 onion, 2 carrots, 2 potatoes', '1/4 cabbage, shredded', '1 tin tomatoes', 'Garlic, dill, sour cream'],
    steps: ['Sauté onion and carrot 5 min.', 'Add beetroot, tomatoes; cook 5 min.', 'Add stock, potatoes, cabbage, beans.', 'Simmer 35-40 min.', 'Garnish with garlic, dill, sour cream.'],
    recovery_note: 'Plant protein from beans plus liver-supporting beetroot — a single bowl that nourishes body and soul.' },
  { id: 'ua2', title: 'Varenyky (Potato & Cheese Dumplings)', country: 'ukraine', category: 'dinner', image: img('1551183053-bf91a1d81141'), prep_minutes: 30, cook_minutes: 15, difficulty: 'medium',
    tags: ['vegetarian', 'budget'],
    ingredients: ['2 cups flour', '3/4 cup warm water, 1 egg, salt', '3 boiled potatoes, mashed', '100g grated cheese', 'Butter, fried onions, sour cream'],
    steps: ['Make dough with flour, water, egg, salt; rest 30 min.', 'Mix mashed potato with cheese.', 'Roll dough thin; cut circles.', 'Fill, fold and seal.', 'Boil 5 min; serve with butter, fried onions, sour cream.'],
    recovery_note: 'Cooking with your hands is meditative — kneading dough and folding dumplings replaces the agitation that often led to drinking.' },
  { id: 'ua3', title: 'Holubtsi (Stuffed Cabbage Rolls)', country: 'ukraine', category: 'dinner', image: img('1605791770440-cb8e64f88a36'), prep_minutes: 20, cook_minutes: 60, difficulty: 'medium',
    tags: ['high-protein', 'gluten-free', 'budget'],
    ingredients: ['1 large cabbage', '300g lean mince', '1/2 cup rice', '1 onion, garlic', '1 tin tomatoes', 'Bay leaf, dill, salt'],
    steps: ['Blanch cabbage; separate leaves.', 'Mix mince with rice, onion, garlic, dill.', 'Roll filling in cabbage leaves.', 'Place in pot; cover with tomatoes and stock.', 'Simmer 50 min.'],
    recovery_note: 'Slow-cooked one-pot meal — the kind of investment in yourself that addiction never allowed.' },
  { id: 'ua4', title: 'Deruny (Ukrainian Potato Pancakes)', country: 'ukraine', category: 'breakfast', image: img('1528207776546-365bb710ee93'), prep_minutes: 10, cook_minutes: 15, difficulty: 'easy',
    tags: ['vegetarian', 'budget'],
    ingredients: ['4 potatoes, grated and squeezed dry', '1 onion, grated', '1 egg', '2 tbsp flour', 'Salt, pepper', 'Sour cream to serve'],
    steps: ['Combine grated potato and onion; squeeze excess water.', 'Mix with egg, flour, salt, pepper.', 'Fry spoonfuls in oil 3-4 min per side.', 'Drain on paper.', 'Serve hot with sour cream.'],
    recovery_note: 'Comfort food that delivers on the promise — crispy, savoury, satisfying without the morning regret.' },
  { id: 'ua5', title: 'Ukrainian Beetroot Salad with Walnuts', country: 'ukraine', category: 'snacks', image: img('1568625365131-079e026a927d'), prep_minutes: 5, cook_minutes: 30, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free'],
    ingredients: ['3 beetroot, roasted and diced', '1/4 cup walnuts, chopped', '1 garlic clove, minced', '2 tbsp olive oil', 'Lemon juice, salt, dill'],
    steps: ['Roast beetroot in foil at 200°C for 45 min.', 'Cool, peel and dice.', 'Whisk garlic, oil, lemon, salt.', 'Toss with beetroot and walnuts.', 'Top with dill.'],
    recovery_note: 'Beetroot + walnuts = liver and brain support in one bowl. Both organs need years of dedicated repair after substance use.' },
  { id: 'ua6', title: 'Kotleta po-Kyivsky (Garlic Herb Chicken)', country: 'ukraine', category: 'dinner', image: img('1532550907401-a500c9a57435'), prep_minutes: 15, cook_minutes: 25, difficulty: 'medium',
    tags: ['high-protein'],
    ingredients: ['2 chicken breasts', '50g butter, softened', '2 cloves garlic, parsley', '1 egg, breadcrumbs', 'Salt, pepper, oil'],
    steps: ['Mix butter with garlic and parsley; freeze 15 min.', 'Pound chicken; place butter on top, roll up.', 'Coat in egg and breadcrumbs.', 'Bake 200°C for 25 min until golden.', 'Serve with mashed potato or salad.'],
    recovery_note: 'A taste-of-home dinner that connects you to cultural heritage — a powerful identity anchor in early Recovery.' },
  { id: 'ua7', title: 'Mlyntsi (Ukrainian Crepes)', country: 'ukraine', category: 'breakfast', image: img('1528207776546-365bb710ee93'), prep_minutes: 10, cook_minutes: 15, difficulty: 'easy',
    tags: ['vegetarian'],
    ingredients: ['1 cup flour', '2 eggs', '1 1/2 cups milk', 'Pinch of salt', 'Butter for pan', 'Honey or fresh berries to serve'],
    steps: ['Whisk flour, eggs, milk, salt until smooth.', 'Heat a small pan, brush with butter.', 'Pour thin layer of batter; cook 1 min per side.', 'Stack as you go.', 'Serve rolled with honey or berries.'],
    recovery_note: 'Make-ahead breakfast — fold them with cottage cheese or fruit for quick mornings when willpower is low.' },
  { id: 'ua8', title: 'Ukrainian Mushroom Soup', country: 'ukraine', category: 'lunch', image: img('1547308283-b941a571d40b'), prep_minutes: 10, cook_minutes: 35, difficulty: 'easy',
    tags: ['vegetarian', 'budget'],
    ingredients: ['500g mushrooms, sliced', '1 onion, 1 carrot, 2 potatoes', '4 cups stock', '1/4 cup pearl barley', 'Bay leaf, dill, sour cream'],
    steps: ['Sauté onion and carrot 5 min.', 'Add mushrooms; cook 8 min.', 'Add potatoes, stock, barley, bay leaf.', 'Simmer 25 min.', 'Top with dill and sour cream.'],
    recovery_note: 'Mushrooms produce vitamin D and B-vitamins — both commonly low in long-term substance users and crucial for mood.' },
  { id: 'ua9', title: 'Salat Vinegret (Vegetable Salad)', country: 'ukraine', category: 'lunch', image: img('1540420773420-3366772f4999'), prep_minutes: 15, cook_minutes: 25, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'budget'],
    ingredients: ['2 boiled beetroot, diced', '2 boiled potatoes, diced', '1 boiled carrot, diced', '1/2 cup pickles, diced', '1/2 cup peas', 'Sunflower oil, vinegar, dill'],
    steps: ['Boil and dice beetroot, potatoes, carrot.', 'Combine all vegetables in a bowl.', 'Dress with oil and vinegar.', 'Season; sprinkle dill.', 'Serve cold.'],
    recovery_note: 'A traditional cold salad — root vegetables deliver steady carbs and minerals to support nervous-system stability.' },
  { id: 'ua10', title: 'Uzvar (Dried Fruit Compote)', country: 'ukraine', category: 'smoothies', image: img('1502741338009-cac2772e18bc'), prep_minutes: 5, cook_minutes: 20, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free'],
    ingredients: ['200g mixed dried fruit (apples, pears, plums, raisins)', '6 cups water', '2 tbsp honey', '1 cinnamon stick', '4 cloves'],
    steps: ['Rinse dried fruit thoroughly.', 'Bring water and spices to boil.', 'Add fruit; simmer 20 min.', 'Stir in honey when slightly cool.', 'Strain or serve with fruit.'],
    recovery_note: 'A traditional non-alcoholic Christmas drink — proves that ritual celebrations can absolutely happen without alcohol.' },

  // ───────────── PORTUGAL (10) ─────────────
  { id: 'pt1', title: 'Caldo Verde (Kale & Potato Soup)', country: 'portugal', category: 'lunch', image: img('1547308283-b941a571d40b'), prep_minutes: 10, cook_minutes: 30, difficulty: 'easy',
    tags: ['vegetarian', 'budget'],
    ingredients: ['4 potatoes, peeled and chopped', '1 onion, 4 garlic cloves', '4 cups stock', '2 cups kale, finely shredded', '1/4 cup olive oil', 'Salt, pepper'],
    steps: ['Sauté onion and garlic 5 min.', 'Add potatoes and stock; simmer 20 min.', 'Blend until smooth.', 'Stir in kale; cook 3 min.', 'Drizzle with olive oil; season.'],
    recovery_note: 'Iron-rich kale and potassium-rich potato — restores minerals lost through years of poor nutrition during active use.' },
  { id: 'pt2', title: 'Bacalhau à Brás (Cod with Eggs)', country: 'portugal', category: 'dinner', image: img('1485921325833-c519f76c4927'), prep_minutes: 15, cook_minutes: 20, difficulty: 'medium',
    tags: ['high-protein', 'omega-3'],
    ingredients: ['400g cod fillet, flaked', '4 eggs', '2 onions, sliced', '300g matchstick potatoes', 'Olive oil, parsley, olives'],
    steps: ['Sauté onions in oil until soft 8 min.', 'Add cod; cook 3 min.', 'Stir in cooked matchstick potatoes.', 'Pour beaten eggs over; stir until just set.', 'Top with parsley and olives.'],
    recovery_note: 'Omega-3 from cod plus complete protein from eggs — a brain-supporting Portuguese classic for any time of day.' },
  { id: 'pt3', title: 'Portuguese Piri-Piri Chicken', country: 'portugal', category: 'dinner', image: img('1532550907401-a500c9a57435'), prep_minutes: 10, cook_minutes: 30, difficulty: 'easy',
    tags: ['high-protein', 'gluten-free'],
    ingredients: ['600g chicken thighs', '4 garlic cloves, minced', '2 tbsp piri-piri sauce or chilli flakes', '1 lemon, juiced', 'Olive oil, paprika, salt'],
    steps: ['Marinate chicken with garlic, piri-piri, lemon, oil 30 min.', 'Bake at 200°C for 30 min until cooked through.', 'Brush with juices halfway.', 'Rest 5 min.', 'Serve with salad and rice.'],
    recovery_note: 'Spicy food triggers natural endorphins — a healthy hedonic upgrade from the chemical highs of substance use.' },
  { id: 'pt4', title: 'Arroz de Tomate (Tomato Rice)', country: 'portugal', category: 'lunch', image: img('1546069901-ba9599a7e63c'), prep_minutes: 5, cook_minutes: 25, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'budget', 'quick'],
    ingredients: ['1 cup rice', '1 tin tomatoes', '1 onion, 2 garlic cloves', '2 cups stock', '1 tsp paprika', 'Olive oil, basil, salt'],
    steps: ['Sauté onion and garlic 4 min.', 'Add tomatoes and paprika; cook 5 min.', 'Stir in rice and stock.', 'Simmer covered 20 min until rice is tender.', 'Top with basil.'],
    recovery_note: 'Pantry-staple Portuguese comfort food — the kind of cheap, nourishing meal that proves Recovery doesn\'t require expensive ingredients.' },
  { id: 'pt5', title: 'Sardinhas Assadas (Grilled Sardines)', country: 'portugal', category: 'dinner', image: img('1467003909585-2f8a72700288'), prep_minutes: 5, cook_minutes: 8, difficulty: 'easy',
    tags: ['high-protein', 'omega-3', 'gluten-free', 'quick'],
    ingredients: ['8 fresh or tinned sardines', '2 lemons', 'Sea salt, olive oil', 'Crusty bread', 'Mixed leaves to serve'],
    steps: ['Pat sardines dry; sprinkle with sea salt.', 'Grill 3-4 min per side until charred.', 'Squeeze lemon over.', 'Serve on bread with leaves.', 'Drizzle with olive oil.'],
    recovery_note: 'Tinned sardines are the cheapest source of omega-3 EPA/DHA on the planet — twice a week is one of the highest-evidence mood-supporting habits.' },
  { id: 'pt6', title: 'Portuguese Bean Stew (Feijoada Vegetariana)', country: 'portugal', category: 'dinner', image: img('1543339308-43e59d6b73a6'), prep_minutes: 10, cook_minutes: 30, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'budget', 'high-fibre'],
    ingredients: ['2 tins white beans', '1 onion, 3 garlic cloves', '1 carrot, 1 capsicum, diced', '1 tin tomatoes', '1 tsp paprika, bay leaf', 'Coriander, olive oil'],
    steps: ['Sauté onion, garlic, carrot, capsicum 8 min.', 'Add beans, tomatoes, paprika, bay.', 'Simmer 25 min.', 'Stir in fresh coriander.', 'Serve with rice or crusty bread.'],
    recovery_note: 'Plant-based comfort with serious staying power — beans deliver fibre that feeds gut bacteria, which in turn make 90% of your serotonin.' },
  { id: 'pt7', title: 'Sopa de Pedra (Stone Soup)', country: 'portugal', category: 'lunch', image: img('1547592180-85f173990554'), prep_minutes: 15, cook_minutes: 40, difficulty: 'easy',
    tags: ['budget', 'high-protein'],
    ingredients: ['1 tin red beans', '200g chorizo or smoked sausage', '1 onion, 2 potatoes, 2 carrots', '1/4 cabbage, 4 cups stock', 'Bay leaf, parsley'],
    steps: ['Sauté chorizo and onion 5 min.', 'Add diced vegetables and stock.', 'Simmer 30 min.', 'Add beans and bay; cook 5 more min.', 'Top with parsley.'],
    recovery_note: 'A "throw what you have" soup — practical Recovery lesson: you don\'t need perfect ingredients to make something nourishing.' },
  { id: 'pt8', title: 'Portuguese Egg Custard (Healthy Pastel de Nata Cup)', country: 'portugal', category: 'snacks', image: img('1488477181946-6428a0291777'), prep_minutes: 10, cook_minutes: 25, difficulty: 'medium',
    tags: ['vegetarian', 'high-protein'],
    ingredients: ['3 egg yolks', '1 cup milk', '2 tbsp cornflour', '3 tbsp honey', '1 tsp vanilla', 'Cinnamon to dust'],
    steps: ['Whisk yolks, cornflour and milk in a pot.', 'Cook over low heat, stirring, until thick.', 'Stir in vanilla and honey.', 'Pour into ramekins; bake 200°C for 15 min.', 'Cool; dust with cinnamon.'],
    recovery_note: 'A treat that hits the sweet-fat reward circuit honestly — proof that pleasure doesn\'t need to come in a bottle.' },
  { id: 'pt9', title: 'Portuguese Tuna Salad', country: 'portugal', category: 'lunch', image: img('1505253758473-96b7015fcd40'), prep_minutes: 10, cook_minutes: 0, difficulty: 'easy',
    tags: ['high-protein', 'omega-3', 'budget', 'quick'],
    ingredients: ['1 tin tuna in olive oil', '1 boiled egg, sliced', '1 boiled potato, cubed', '1 tomato, 1/4 onion', '10 olives, parsley', 'Olive oil, vinegar'],
    steps: ['Layer potato, tuna, egg, tomato, onion in a bowl.', 'Add olives and parsley.', 'Whisk olive oil and vinegar.', 'Pour over.', 'Serve immediately.'],
    recovery_note: 'A complete one-bowl lunch — protein, healthy fat, complex carbs and minerals in 10 minutes.' },
  { id: 'pt10', title: 'Açorda (Bread & Garlic Soup)', country: 'portugal', category: 'breakfast', image: img('1547308283-b941a571d40b'), prep_minutes: 5, cook_minutes: 10, difficulty: 'easy',
    tags: ['vegetarian', 'budget'],
    ingredients: ['4 slices day-old wholegrain bread, cubed', '4 garlic cloves, minced', '4 cups vegetable stock', '2 eggs', 'Olive oil, coriander, salt'],
    steps: ['Sauté garlic in olive oil 1 min.', 'Add stock and bread; simmer 5 min.', 'Crack eggs in; poach 3 min.', 'Top with coriander.', 'Serve hot.'],
    recovery_note: 'Cheap, traditional and warming — a Portuguese reminder that the simplest meals often nourish best.' },

  // ───────────── FRANCE (10) ─────────────
  { id: 'fr1', title: 'Ratatouille', country: 'france', category: 'dinner', image: img('1574894709920-11b28e7367e3'), prep_minutes: 15, cook_minutes: 35, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'budget'],
    ingredients: ['1 eggplant, 1 zucchini, cubed', '1 capsicum, 1 onion, sliced', '4 garlic cloves, 1 tin tomatoes', 'Herbes de Provence, olive oil', 'Salt, pepper, basil'],
    steps: ['Sauté onion, garlic, capsicum 5 min.', 'Add eggplant and zucchini; cook 10 min.', 'Stir in tomatoes and herbs.', 'Simmer 20 min.', 'Top with basil; serve with bread or rice.'],
    recovery_note: 'A vegetable-forward classic that proves "healthy" can also be deeply French and indulgent.' },
  { id: 'fr2', title: 'Niçoise Salad', country: 'france', category: 'lunch', image: img('1540420773420-3366772f4999'), prep_minutes: 15, cook_minutes: 10, difficulty: 'easy',
    tags: ['high-protein', 'omega-3', 'gluten-free'],
    ingredients: ['1 tin tuna or 1 cooked salmon fillet', '2 boiled eggs, halved', '200g green beans, blanched', '6 baby potatoes, boiled', 'Olives, tomatoes, lettuce', 'Vinaigrette'],
    steps: ['Boil eggs and potatoes; blanch beans.', 'Arrange leaves on a platter.', 'Top with potatoes, beans, eggs, tuna.', 'Add olives and tomatoes.', 'Drizzle with vinaigrette.'],
    recovery_note: 'A composed salad you build with care — slow assembly is its own form of meditation.' },
  { id: 'fr3', title: 'Poulet à la Provençale (Provençal Chicken)', country: 'france', category: 'dinner', image: img('1532550907401-a500c9a57435'), prep_minutes: 10, cook_minutes: 30, difficulty: 'easy',
    tags: ['high-protein', 'gluten-free'],
    ingredients: ['4 chicken thighs', '1 tin tomatoes', '1 onion, 4 garlic cloves', '1/2 cup olives', 'Herbes de Provence, olive oil', 'Salt, pepper, basil'],
    steps: ['Brown chicken in oil 5 min per side.', 'Add onion and garlic; cook 3 min.', 'Add tomatoes, olives, herbs.', 'Simmer 25 min.', 'Top with basil; serve with rice or bread.'],
    recovery_note: 'A French classic without wine — proves that sober French food still tastes unmistakably French.' },
  { id: 'fr4', title: 'French Onion Soup (Alcohol-Free)', country: 'france', category: 'lunch', image: img('1547308283-b941a571d40b'), prep_minutes: 10, cook_minutes: 50, difficulty: 'medium',
    tags: ['vegetarian'],
    ingredients: ['4 large onions, thinly sliced', '4 cups beef or vegetable stock', '2 tbsp butter', '1 tbsp Worcestershire sauce', '4 slices baguette', 'Gruyère cheese'],
    steps: ['Caramelise onions slowly in butter 30 min.', 'Add stock and Worcestershire; simmer 15 min.', 'Toast baguette slices.', 'Ladle soup into bowls; top with bread and cheese.', 'Grill until cheese melts.'],
    recovery_note: 'Patience caramelising onions is a Recovery lesson in miniature — what looks slow is actually transformation.' },
  { id: 'fr5', title: 'Quiche Lorraine (Bacon & Cheese Tart)', country: 'france', category: 'lunch', image: img('1525351484163-7529414344d8'), prep_minutes: 15, cook_minutes: 35, difficulty: 'medium',
    tags: ['high-protein'],
    ingredients: ['1 pre-made shortcrust pastry', '4 eggs, 1 cup cream', '150g bacon, chopped', '1 cup grated Gruyère', 'Salt, pepper, nutmeg'],
    steps: ['Press pastry into a tart tin.', 'Cook bacon until crisp.', 'Whisk eggs, cream, salt, nutmeg.', 'Sprinkle bacon and cheese in pastry.', 'Pour egg mixture; bake 180°C for 30 min.'],
    recovery_note: 'High-protein, satiating French classic — eat with green salad for a balanced lunch that lasts hours.' },
  { id: 'fr6', title: 'Crêpes (Sweet or Savoury)', country: 'france', category: 'breakfast', image: img('1528207776546-365bb710ee93'), prep_minutes: 10, cook_minutes: 15, difficulty: 'easy',
    tags: ['vegetarian'],
    ingredients: ['1 cup flour', '2 eggs', '1 1/2 cups milk', '2 tbsp melted butter', 'Pinch salt', 'Fillings: ham, cheese, eggs OR fruit, honey'],
    steps: ['Whisk batter until smooth; rest 30 min.', 'Heat a pan; brush with butter.', 'Pour thin layer; cook 1 min per side.', 'Fill with savoury or sweet ingredients.', 'Fold and serve.'],
    recovery_note: 'Versatile foundation — mass-cook on Sunday, fill differently each weekday morning.' },
  { id: 'fr7', title: 'Salade Lyonnaise', country: 'france', category: 'lunch', image: img('1540420773420-3366772f4999'), prep_minutes: 10, cook_minutes: 10, difficulty: 'easy',
    tags: ['high-protein'],
    ingredients: ['Frisée or curly endive', '4 rashers bacon', '2 eggs', '1 tbsp Dijon, vinegar, oil', '1 garlic clove', 'Croutons'],
    steps: ['Cook bacon until crisp.', 'Poach eggs 3 min.', 'Whisk Dijon, vinegar, oil for vinaigrette.', 'Toss leaves with vinaigrette.', 'Top with bacon, croutons, poached egg.'],
    recovery_note: 'Restaurant-quality salad in 20 minutes at home — saving money and avoiding the bar that comes with eating out.' },
  { id: 'fr8', title: 'Croque Monsieur', country: 'france', category: 'lunch', image: img('1539252554935-80c8cabf01ab'), prep_minutes: 10, cook_minutes: 8, difficulty: 'easy',
    tags: ['high-protein', 'quick'],
    ingredients: ['4 slices wholegrain bread', '4 slices ham', '100g grated Gruyère', '2 tbsp butter', '2 tbsp flour, 1 cup milk', 'Salt, nutmeg'],
    steps: ['Make béchamel: butter, flour, milk, salt, nutmeg, 5 min.', 'Spread bread with béchamel.', 'Add ham and cheese; close.', 'Pan-toast 3 min per side.', 'Serve hot.'],
    recovery_note: 'A French street-food classic that satisfies the salt+fat+savoury reward circuits without the late-night kebab.' },
  { id: 'fr9', title: 'Tarte aux Tomates (Tomato Tart)', country: 'france', category: 'dinner', image: img('1574894709920-11b28e7367e3'), prep_minutes: 15, cook_minutes: 35, difficulty: 'easy',
    tags: ['vegetarian'],
    ingredients: ['1 sheet puff pastry', '4 ripe tomatoes, sliced', '2 tbsp Dijon mustard', '100g grated cheese', 'Olive oil, basil, salt'],
    steps: ['Roll out pastry on a tray; score 1cm border.', 'Spread Dijon inside the border.', 'Layer tomatoes; sprinkle cheese.', 'Drizzle olive oil; bake 200°C for 30 min.', 'Top with basil before serving.'],
    recovery_note: 'A 5-ingredient dinner that looks fancy — the kind of small win that rebuilds self-respect in early sobriety.' },
  { id: 'fr10', title: 'French Mushroom Soup', country: 'france', category: 'lunch', image: img('1547308283-b941a571d40b'), prep_minutes: 10, cook_minutes: 25, difficulty: 'easy',
    tags: ['vegetarian'],
    ingredients: ['500g mushrooms, sliced', '1 onion, 2 garlic cloves', '4 cups stock', '1/2 cup cream', '2 tbsp butter, thyme, salt'],
    steps: ['Sauté onion and garlic in butter 5 min.', 'Add mushrooms and thyme; cook 10 min.', 'Add stock; simmer 10 min.', 'Blend smooth; stir in cream.', 'Season and serve with bread.'],
    recovery_note: 'Earthy, grounding flavours and umami satisfaction — mushrooms also deliver vitamin D, often deficient after years indoors.' },

  // ───────────── ITALY (10) ─────────────
  { id: 'it1', title: 'Pasta al Pomodoro', country: 'italy', category: 'dinner', image: img('1551183053-bf91a1d81141'), prep_minutes: 5, cook_minutes: 25, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'budget', 'quick'],
    ingredients: ['400g spaghetti', '1 tin San Marzano tomatoes', '4 garlic cloves, sliced', '1/4 cup olive oil', 'Fresh basil, salt', 'Parmesan optional'],
    steps: ['Cook pasta per packet.', 'Sauté garlic in oil 1 min.', 'Add tomatoes; simmer 15 min.', 'Toss with cooked pasta.', 'Top with basil.'],
    recovery_note: 'The simplest Italian classic and the highest-evidence Mediterranean staple — affordable, repeatable, comforting.' },
  { id: 'it2', title: 'Risotto al Funghi (Mushroom Risotto)', country: 'italy', category: 'dinner', image: img('1476124369491-e7addf5db371'), prep_minutes: 10, cook_minutes: 25, difficulty: 'medium',
    tags: ['vegetarian', 'gluten-free'],
    ingredients: ['1 cup arborio rice', '500g mushrooms, sliced', '1 onion, 2 garlic cloves', '4 cups warm vegetable stock', 'Parmesan, butter, olive oil'],
    steps: ['Sauté onion and mushrooms 8 min.', 'Add rice; toast 1 min.', 'Add stock one ladle at a time, stirring.', 'After 18 min rice is creamy.', 'Stir in butter and Parmesan.'],
    recovery_note: 'The slow stirring of risotto is a meditative ritual — mindfulness with dinner at the end.' },
  { id: 'it3', title: 'Minestrone', country: 'italy', category: 'lunch', image: img('1547592180-85f173990554'), prep_minutes: 15, cook_minutes: 35, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'budget', 'high-fibre'],
    ingredients: ['1 onion, 2 carrots, 2 celery, diced', '2 garlic cloves, 1 zucchini, 1 potato', '1 tin tomatoes, 1 tin beans', '1/2 cup small pasta', '6 cups stock, herbs'],
    steps: ['Sauté onion, carrot, celery, garlic 5 min.', 'Add remaining vegetables, tomatoes, stock, herbs.', 'Simmer 20 min.', 'Add beans and pasta; cook 10 more min.', 'Top with parmesan.'],
    recovery_note: 'Eight different vegetables in one bowl — the antioxidant variety supports brain repair after long-term substance damage.' },
  { id: 'it4', title: 'Pasta Carbonara (Lighter Version)', country: 'italy', category: 'dinner', image: img('1551183053-bf91a1d81141'), prep_minutes: 5, cook_minutes: 15, difficulty: 'medium',
    tags: ['high-protein'],
    ingredients: ['400g spaghetti', '150g pancetta or bacon', '4 egg yolks, 1 whole egg', '1 cup grated Pecorino', 'Black pepper, salt'],
    steps: ['Cook pasta; reserve 1/2 cup cooking water.', 'Crisp pancetta in pan.', 'Whisk eggs and Pecorino.', 'Toss hot pasta with pancetta off heat.', 'Stir in egg mixture and pasta water for a creamy sauce.'],
    recovery_note: 'A Roman classic done right — high protein, satisfying, and shows that comfort food is part of Recovery, not against it.' },
  { id: 'it5', title: 'Pizza Margherita (Homemade)', country: 'italy', category: 'dinner', image: img('1574894709920-11b28e7367e3'), prep_minutes: 30, cook_minutes: 12, difficulty: 'medium',
    tags: ['vegetarian'],
    ingredients: ['Pizza dough (shop-bought or homemade)', '1/2 cup tomato passata', '200g fresh mozzarella, sliced', 'Fresh basil', 'Olive oil, salt'],
    steps: ['Preheat oven to 240°C.', 'Roll dough on floured surface.', 'Spread passata; add mozzarella.', 'Bake 10-12 min until crust is crisp.', 'Top with basil and olive oil.'],
    recovery_note: 'Pizza night at home replaces pizza-and-beer at the pub — a small swap that protects your sobriety weekly.' },
  { id: 'it6', title: 'Bruschetta al Pomodoro', country: 'italy', category: 'snacks', image: img('1539252554935-80c8cabf01ab'), prep_minutes: 10, cook_minutes: 5, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'quick'],
    ingredients: ['4 slices sourdough, toasted', '4 ripe tomatoes, diced', '2 garlic cloves', 'Fresh basil', 'Extra virgin olive oil, salt'],
    steps: ['Toast bread until golden.', 'Rub with garlic clove while warm.', 'Mix tomatoes, basil, oil, salt.', 'Spoon onto toast.', 'Serve immediately.'],
    recovery_note: 'A 10-minute Italian appetiser that shows hospitality without alcohol — perfect for sober dinner parties.' },
  { id: 'it7', title: 'Pasta al Pesto', country: 'italy', category: 'dinner', image: img('1551183053-bf91a1d81141'), prep_minutes: 10, cook_minutes: 12, difficulty: 'easy',
    tags: ['vegetarian', 'budget', 'quick'],
    ingredients: ['400g pasta', '2 cups fresh basil', '1/2 cup pine nuts (or walnuts)', '2 garlic cloves', '1/2 cup Parmesan', '1/2 cup olive oil'],
    steps: ['Cook pasta per packet.', 'Blend basil, nuts, garlic, Parmesan, oil.', 'Reserve 1/4 cup pasta water.', 'Toss pasta with pesto and water.', 'Top with extra Parmesan.'],
    recovery_note: 'Walnut pesto is rich in omega-3s — a brain-supporting twist on the Italian classic.' },
  { id: 'it8', title: 'Insalata di Farro (Spelt Grain Salad)', country: 'italy', category: 'lunch', image: img('1540420773420-3366772f4999'), prep_minutes: 10, cook_minutes: 25, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'high-fibre'],
    ingredients: ['1 cup farro or pearl barley', '1 cucumber, 1 capsicum, diced', '1 cup cherry tomatoes, halved', '1/2 cup feta', 'Olives, basil, lemon, oil'],
    steps: ['Cook farro per packet; cool.', 'Combine with diced vegetables.', 'Add feta and olives.', 'Whisk lemon and oil.', 'Toss; top with basil.'],
    recovery_note: 'Ancient grains pack more nutrients than refined ones — particularly fibre and B-vitamins for steady energy.' },
  { id: 'it9', title: 'Polpette in Salsa di Pomodoro (Meatballs)', country: 'italy', category: 'dinner', image: img('1551183053-bf91a1d81141'), prep_minutes: 15, cook_minutes: 30, difficulty: 'easy',
    tags: ['high-protein'],
    ingredients: ['500g lean mince', '1 egg, 1/2 cup breadcrumbs', '1/4 cup grated Parmesan', '1 onion, garlic', '1 tin tomatoes, basil, oregano'],
    steps: ['Mix mince, egg, breadcrumbs, Parmesan; form balls.', 'Brown meatballs in oil 5 min.', 'Sauté onion and garlic; add tomatoes and herbs.', 'Return meatballs; simmer 20 min.', 'Serve with pasta or polenta.'],
    recovery_note: 'Iron-rich red meat plus tomato sauce — a Sunday-style meal that feeds 4 and keeps for two days of leftovers.' },
  { id: 'it10', title: 'Caprese di Bufala', country: 'italy', category: 'snacks', image: img('1572441713132-51c75654db73'), prep_minutes: 5, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'gluten-free', 'quick'],
    ingredients: ['1 ball fresh buffalo mozzarella', '3 ripe tomatoes, sliced', 'Fresh basil leaves', 'Extra virgin olive oil', 'Sea salt, balsamic glaze'],
    steps: ['Slice mozzarella and tomatoes thickly.', 'Alternate on a plate.', 'Tuck basil between slices.', 'Drizzle with oil and balsamic.', 'Sprinkle sea salt; serve.'],
    recovery_note: 'Three ingredients, restaurant-quality plate — proves that quality beats complexity, in food as in Recovery.' },

  // ───────────── SPAIN (10) ─────────────
  { id: 'es1', title: 'Paella Valenciana (Simplified)', country: 'spain', category: 'dinner', image: img('1565299624946-b28f40a0ae38'), prep_minutes: 15, cook_minutes: 30, difficulty: 'medium',
    tags: ['high-protein', 'gluten-free'],
    ingredients: ['1.5 cups paella or short-grain rice', '300g chicken thigh, cubed', '200g prawns', '1 onion, capsicum, garlic', '3 cups stock, saffron', 'Lemon, parsley, olive oil'],
    steps: ['Brown chicken in paella pan.', 'Add onion, capsicum, garlic; cook 5 min.', 'Stir in rice and saffron stock.', 'Simmer 15 min without stirring.', 'Add prawns last 5 min; rest before serving.'],
    recovery_note: 'A celebratory one-pan dish — proves family meals can be the highlight of sober Sundays.' },
  { id: 'es2', title: 'Gazpacho (Cold Tomato Soup)', country: 'spain', category: 'lunch', image: img('1547592180-85f173990554'), prep_minutes: 10, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'budget', 'quick'],
    ingredients: ['1kg ripe tomatoes', '1 cucumber, 1 capsicum', '1 garlic clove, 1/2 onion', '3 tbsp olive oil, 2 tbsp vinegar', 'Salt, ice water'],
    steps: ['Roughly chop all vegetables.', 'Blend with garlic, oil, vinegar, salt.', 'Add ice water for desired consistency.', 'Strain for silky texture.', 'Chill 1 hour; serve cold.'],
    recovery_note: 'Hydrating and antioxidant-packed — perfect for hot afternoons when alcohol\'s old "cooling" promise can resurface.' },
  { id: 'es3', title: 'Tortilla Española (Spanish Omelette)', country: 'spain', category: 'breakfast', image: img('1525351484163-7529414344d8'), prep_minutes: 10, cook_minutes: 25, difficulty: 'medium',
    tags: ['vegetarian', 'high-protein', 'gluten-free', 'budget'],
    ingredients: ['4 potatoes, thinly sliced', '1 large onion, sliced', '6 eggs', 'Olive oil, salt'],
    steps: ['Slowly fry potatoes and onion in oil 20 min until soft.', 'Drain; mix with beaten eggs and salt.', 'Pour into hot pan; cook 5 min.', 'Flip with a plate; cook 3 more min.', 'Serve warm or cold.'],
    recovery_note: 'Pantry-staple meal that feeds 4 — Spanish home cooking at its most economical and satisfying.' },
  { id: 'es4', title: 'Patatas Bravas', country: 'spain', category: 'snacks', image: img('1623428187969-5da2dcea5ebf'), prep_minutes: 10, cook_minutes: 30, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'budget'],
    ingredients: ['4 potatoes, cubed', '1 tin tomatoes', '1 tsp paprika, 1/2 tsp chilli flakes', '2 garlic cloves', 'Olive oil, salt, mayo'],
    steps: ['Roast potatoes at 220°C for 25 min until crisp.', 'Sauté garlic; add tomatoes, paprika, chilli.', 'Simmer 10 min.', 'Drizzle bravas sauce over potatoes.', 'Serve with mayo on the side.'],
    recovery_note: 'A Spanish tapas classic — small, shareable plates create the social ritual that bars used to provide.' },
  { id: 'es5', title: 'Pollo al Ajillo (Garlic Chicken)', country: 'spain', category: 'dinner', image: img('1532550907401-a500c9a57435'), prep_minutes: 5, cook_minutes: 25, difficulty: 'easy',
    tags: ['high-protein', 'gluten-free'],
    ingredients: ['600g chicken thighs', '8 garlic cloves, sliced', '1/4 cup olive oil', '1 tsp paprika, dried chilli', 'Lemon, parsley, salt'],
    steps: ['Brown chicken in oil 8 min per side.', 'Add garlic, paprika, chilli; cook 3 min.', 'Squeeze lemon; cover and simmer 10 min.', 'Top with parsley.', 'Serve with bread to soak up juices.'],
    recovery_note: 'Garlic supports cardiovascular health — important since long-term substance use stresses the heart and circulation.' },
  { id: 'es6', title: 'Lentejas Estofadas (Lentil Stew)', country: 'spain', category: 'lunch', image: img('1547592180-85f173990554'), prep_minutes: 10, cook_minutes: 35, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'budget', 'high-fibre'],
    ingredients: ['1 cup brown lentils', '1 onion, 2 garlic cloves', '1 carrot, 1 capsicum, 1 potato', '1 tin tomatoes', 'Bay leaf, paprika, cumin'],
    steps: ['Sauté onion, garlic, carrot, capsicum 5 min.', 'Add lentils, potato, tomatoes, spices, 4 cups water.', 'Simmer 30 min until lentils are tender.', 'Season; remove bay leaf.', 'Serve with crusty bread.'],
    recovery_note: 'A Spanish grandmother\'s remedy for whatever ails you — high in folate which supports neurotransmitter production.' },
  { id: 'es7', title: 'Pisto Manchego (Spanish Vegetable Stew)', country: 'spain', category: 'dinner', image: img('1574894709920-11b28e7367e3'), prep_minutes: 15, cook_minutes: 30, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'budget'],
    ingredients: ['1 zucchini, 1 eggplant, cubed', '2 capsicums, 1 onion, sliced', '1 tin tomatoes', 'Olive oil, garlic, paprika, salt', 'Eggs to top (optional)'],
    steps: ['Sauté onion, capsicum 5 min.', 'Add eggplant and zucchini; cook 10 min.', 'Stir in tomatoes, garlic, paprika.', 'Simmer 15 min.', 'Top with fried egg if desired.'],
    recovery_note: 'Spain\'s answer to ratatouille — vegetable-forward, satisfying and very forgiving for beginner cooks.' },
  { id: 'es8', title: 'Garbanzos con Espinacas (Chickpea & Spinach)', country: 'spain', category: 'lunch', image: img('1543339308-43e59d6b73a6'), prep_minutes: 5, cook_minutes: 15, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'budget', 'quick'],
    ingredients: ['1 tin chickpeas, rinsed', '500g baby spinach', '4 garlic cloves, sliced', '1 tsp cumin, 1 tsp paprika', 'Olive oil, salt, sherry vinegar'],
    steps: ['Sauté garlic in oil 1 min.', 'Add cumin and paprika; toast.', 'Add chickpeas and 1/4 cup water; simmer 5 min.', 'Stir in spinach until wilted.', 'Finish with sherry vinegar.'],
    recovery_note: 'Iron from spinach plus protein from chickpeas in 20 minutes — the kind of meal that makes plant-based eating feel powerful, not restrictive.' },
  { id: 'es9', title: 'Pan con Tomate', country: 'spain', category: 'breakfast', image: img('1539252554935-80c8cabf01ab'), prep_minutes: 5, cook_minutes: 3, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'budget', 'quick'],
    ingredients: ['4 slices sourdough', '1 garlic clove', '2 ripe tomatoes', 'Extra virgin olive oil', 'Sea salt'],
    steps: ['Toast bread.', 'Rub each slice with garlic clove.', 'Halve tomatoes and rub the pulp into the bread.', 'Drizzle with olive oil.', 'Sprinkle sea salt; serve.'],
    recovery_note: 'A 5-minute Spanish breakfast that delivers polyphenols, lycopene and good fats — start the day right when willpower is at its peak.' },
  { id: 'es10', title: 'Ensalada Mixta (Spanish Mixed Salad)', country: 'spain', category: 'lunch', image: img('1540420773420-3366772f4999'), prep_minutes: 10, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'gluten-free', 'budget', 'quick'],
    ingredients: ['Mixed lettuce', '2 tomatoes, 1/2 cucumber, 1/4 onion', '1 boiled egg, sliced', '1 tin tuna or chickpeas', 'Olives, olive oil, vinegar, salt'],
    steps: ['Toss leaves with vegetables.', 'Top with egg and tuna or chickpeas.', 'Add olives.', 'Whisk olive oil, vinegar, salt.', 'Drizzle and serve.'],
    recovery_note: 'A go-to lunch that takes 10 minutes — habits like these become the structure that protects sobriety.' },

  // ───────────── KOREA (10) ─────────────
  { id: 'kr1', title: 'Bibimbap (Mixed Rice Bowl)', country: 'korea', category: 'dinner', image: img('1546069901-ba9599a7e63c'), prep_minutes: 20, cook_minutes: 15, difficulty: 'medium',
    tags: ['high-protein', 'gluten-free'],
    ingredients: ['2 cups cooked rice', '1 carrot, julienned', '1 zucchini, sliced', '1 cup spinach, blanched', '4 eggs, 200g lean beef strips', 'Soy, sesame oil, gochujang'],
    steps: ['Sauté each vegetable separately with sesame oil and salt.', 'Marinate beef in soy and sugar; stir-fry 3 min.', 'Fry eggs sunny-side up.', 'Layer rice, vegetables, beef, egg in bowls.', 'Top with gochujang; mix at the table.'],
    recovery_note: 'A complete meal in one bowl — protein, fibre, healthy fats and complex carbs that stabilise mood and energy for hours.' },
  { id: 'kr2', title: 'Kimchi Fried Rice', country: 'korea', category: 'lunch', image: img('1546069901-ba9599a7e63c'), prep_minutes: 5, cook_minutes: 10, difficulty: 'easy',
    tags: ['vegetarian', 'budget', 'quick'],
    ingredients: ['2 cups cooked rice', '1 cup kimchi, chopped', '2 spring onions', '2 eggs', 'Sesame oil, soy sauce', 'Nori sheet (optional)'],
    steps: ['Heat oil; sauté spring onion 1 min.', 'Add kimchi; cook 2 min.', 'Add rice; toss to combine.', 'Push to side; scramble eggs in.', 'Drizzle sesame oil and soy; top with nori.'],
    recovery_note: 'Kimchi delivers serious probiotic load — a thriving gut microbiome is one of the strongest predictors of mood resilience.' },
  { id: 'kr3', title: 'Bulgogi (Marinated Beef)', country: 'korea', category: 'dinner', image: img('1582450871972-95dd6e5e0c92'), prep_minutes: 30, cook_minutes: 8, difficulty: 'easy',
    tags: ['high-protein'],
    ingredients: ['500g beef sirloin, thinly sliced', '4 tbsp soy, 2 tbsp brown sugar', '2 tbsp sesame oil, 4 garlic cloves', '1 pear, grated', 'Spring onions, sesame seeds, rice'],
    steps: ['Whisk soy, sugar, sesame oil, garlic, grated pear.', 'Marinate beef 20 min minimum.', 'Heat pan very hot; cook beef in batches 2 min.', 'Top with spring onions and sesame.', 'Serve with rice and lettuce wraps.'],
    recovery_note: 'Iron-rich beef plus the natural sweetness of pear marinade — proves savoury-sweet doesn\'t need refined sugar to satisfy.' },
  { id: 'kr4', title: 'Doenjang Jjigae (Soybean Paste Stew)', country: 'korea', category: 'lunch', image: img('1547592180-85f173990554'), prep_minutes: 10, cook_minutes: 20, difficulty: 'easy',
    tags: ['vegetarian', 'budget'],
    ingredients: ['3 tbsp doenjang (soybean paste)', '1 zucchini, 1 potato, 1 onion', '300g firm tofu, cubed', '1 chilli, mushrooms', '4 cups water, 2 garlic cloves'],
    steps: ['Combine doenjang with water; bring to simmer.', 'Add potato, onion, mushrooms 10 min.', 'Add zucchini, tofu, garlic, chilli; cook 5 min.', 'Adjust seasoning.', 'Serve with rice.'],
    recovery_note: 'Fermented soybean paste delivers umami and probiotics — a flavourful way to support gut health on weeknights.' },
  { id: 'kr5', title: 'Japchae (Glass Noodle Stir-Fry)', country: 'korea', category: 'dinner', image: img('1546069901-ba9599a7e63c'), prep_minutes: 15, cook_minutes: 15, difficulty: 'medium',
    tags: ['vegetarian', 'gluten-free'],
    ingredients: ['200g sweet potato glass noodles', '1 capsicum, 1 carrot, julienned', '1 cup spinach, mushrooms', '2 eggs', 'Soy, sesame oil, sugar, garlic'],
    steps: ['Soak noodles in hot water 10 min; drain.', 'Sauté each vegetable separately in sesame oil.', 'Cook scrambled egg.', 'Toss noodles with soy, sesame, sugar.', 'Combine everything; sprinkle sesame seeds.'],
    recovery_note: 'Festive Korean noodles without alcohol — proves cultural celebration foods translate beautifully to sober tables.' },
  { id: 'kr6', title: 'Pajeon (Korean Spring Onion Pancake)', country: 'korea', category: 'snacks', image: img('1528207776546-365bb710ee93'), prep_minutes: 5, cook_minutes: 10, difficulty: 'easy',
    tags: ['vegetarian', 'budget'],
    ingredients: ['1 cup flour, 1/4 cup cornflour', '1 cup ice water', '1 egg, 1 bunch spring onions', '1 chilli, soy and vinegar dipping sauce'],
    steps: ['Whisk flour, cornflour, water, egg.', 'Lay spring onions in pan; pour batter over.', 'Cook 4 min until golden.', 'Flip; cook 3 more min.', 'Serve with dipping sauce.'],
    recovery_note: 'A traditional Korean rainy-day food — emotional eating taken back from addiction and put to good use.' },
  { id: 'kr7', title: 'Korean Cucumber Salad (Oi Muchim)', country: 'korea', category: 'snacks', image: img('1568625365131-079e026a927d'), prep_minutes: 10, cook_minutes: 0, difficulty: 'easy',
    tags: ['vegetarian', 'vegan', 'gluten-free', 'quick'],
    ingredients: ['2 cucumbers, sliced', '1 tbsp gochugaru (Korean chilli flakes)', '1 tbsp soy, 1 tsp sugar', '1 tbsp rice vinegar, sesame seeds', '2 garlic cloves'],
    steps: ['Slice cucumbers; salt 10 min, then drain.', 'Whisk soy, sugar, vinegar, gochugaru, garlic.', 'Toss with cucumber.', 'Sprinkle sesame seeds.', 'Serve immediately or chilled.'],
    recovery_note: 'Cucumber is 96% water — hydration prevents the headache and irritability that often masquerade as cravings.' },
  { id: 'kr8', title: 'Korean Chicken Soup (Dak Gomtang)', country: 'korea', category: 'lunch', image: img('1547592180-85f173990554'), prep_minutes: 10, cook_minutes: 60, difficulty: 'easy',
    tags: ['high-protein', 'gluten-free'],
    ingredients: ['1 whole chicken or 4 chicken legs', '1 onion, 4 garlic cloves', 'Ginger, spring onions', '1 cup glutinous rice', '2 jujubes (optional)'],
    steps: ['Place chicken with onion, garlic, ginger, jujubes in pot.', 'Cover with water; simmer 50 min.', 'Add rinsed rice last 30 min.', 'Shred chicken; return to broth.', 'Top with spring onions; season at the table.'],
    recovery_note: 'A traditional restorative — slow-cooked broth supports gut healing, often damaged by years of alcohol use.' },
  { id: 'kr9', title: 'Tteokbokki (Spicy Rice Cakes)', country: 'korea', category: 'snacks', image: img('1623428187969-5da2dcea5ebf'), prep_minutes: 5, cook_minutes: 15, difficulty: 'easy',
    tags: ['vegetarian'],
    ingredients: ['400g Korean rice cakes (tteok)', '2 tbsp gochujang', '1 tbsp gochugaru, 1 tbsp soy', '1 tbsp sugar, 1 garlic clove', '2 cups stock, spring onions, sesame'],
    steps: ['Soak rice cakes if dry.', 'Combine sauce with stock; bring to boil.', 'Add rice cakes; simmer 10 min until thick.', 'Stir frequently to prevent sticking.', 'Top with sesame and spring onions.'],
    recovery_note: 'Korean street food at home — replaces late-night drunken snack runs with a cooking ritual you can enjoy fully present.' },
  { id: 'kr10', title: 'Korean Egg Roll (Gyeran-mari)', country: 'korea', category: 'breakfast', image: img('1525351484163-7529414344d8'), prep_minutes: 5, cook_minutes: 10, difficulty: 'medium',
    tags: ['vegetarian', 'high-protein', 'gluten-free', 'quick'],
    ingredients: ['4 eggs', '2 spring onions, finely chopped', '1 carrot, finely diced', '1 tbsp milk, salt', 'Oil for pan'],
    steps: ['Whisk eggs with milk, salt, vegetables.', 'Pour thin layer in oiled pan; cook 1 min.', 'Roll up while still soft.', 'Push to side; pour more egg; let cook then roll over the existing roll.', 'Slice into rounds.'],
    recovery_note: 'A rolled omelette that looks impressive and delivers complete protein — confidence-builder for a still-new cook.' }
);

// Phase 2 & 3 expansion — 50 additional mains + 30 desserts.
DIET_MEALS.push(...DIET_EXPANSION_MAINS, ...DIET_EXPANSION_DESSERTS);

